import { supabase } from "./churchSupabaseClient";
import { fetchOrganizationBranding } from "./churchOrganizationAccessService";
import {
  buildPreServiceLoopItems,
  buildPreServiceLoopItemsForUser,
  buildSurroundingChurchEventsLoopItem,
  loadSharedChurchEventsForAccount,
} from "./churchContentService";
import {
  buildSlidesFromServiceItems,
  getCurrentServiceId,
  loadServiceItems,
} from "./serviceService";
import {
  loadUpcomingWeeklyHymnPlan,
  loadUpcomingWeeklyHymnPlanForDisplay,
} from "./hymnService";
import { loadChurchServiceTimes } from "./churchServiceTimesService";
import { loadMensDutiesForAccount, loadMensDutyTypesForAccount } from "./mensDutiesService";

const LIVE_DISPLAYS_TABLE = "church_live_displays";
const LIVE_SCREENS_TABLE = "church_live_screens";
const TITHING_TABLE = "church_tithing_records";
const ATTENDANCE_TABLE = "church_attendance_records";
const FAMILIES_TABLE = "church_families";
const MEMBERS_TABLE = "church_members";
const MANAGEMENT_MEDIA_BUCKET = "church-media-library";
const STORAGE_KEY_PREFIX = "oikos.church.live-display";
const SCREEN_SESSION_PREFIX = "oikos.church.live-display.screen";
const SCREEN_DEVICE_PREFIX = "oikos.church.live-display.device";
const LIVE_DISPLAY_CONTROL_CHANNEL_PREFIX = "oikos.church.live-display-control";
const LIVE_DISPLAY_CONTROL_EVENT = "display-control";
const FAMILY_IMAGE_SIGNED_URL_CACHE_KEY = "oikos.church.live-display.family-signed-urls";
const ACTIVE_SCREEN_WINDOW_MS = 1000 * 60 * 2;
const FAMILY_IMAGE_SIGNED_URL_CACHE_TTL_MS = 1000 * 60 * 30;
const FAMILY_IMAGE_DISPLAY_LIMIT = 120;
const MEMBER_DIRECTORY_DISPLAY_LIMIT = 200;
const LEGACY_LOOP_TITLE = "Welcome to Service";
const LEGACY_LOOP_BODY = "Announcements and event highlights are looping before the service begins.";
const DEFAULT_LOOP_INTERVAL_SECONDS = 6;
const DEFAULT_SCREEN_GROUP = "service";
const SCREEN_GROUPS = new Set(["service", "interactive"]);
const DEFAULT_INTERACTIVE_CONFIG = {
  apps: {
    "members-directory": true,
    announcements: true,
    events: true,
    "surrounding-events": true,
    "prayer-request": true,
    "visitor-log": true,
    "phone-page": true,
    "mens-duties": true,
    preacher: true,
  },
};
const QUICK_HYMN_SOURCE_PREFIX = "quick-hymn:";
const QUICK_SERMON_SOURCE_PREFIX = "quick-sermon:";
const CURRENT_HYMNS_SLIDE_ID = "current-hymns-slide";
const LEGACY_TODAY_SONGS_SLIDE_ID = "todays-songs-slide";
const TITHING_BREAKDOWN_SLIDE_ID = "tithing-breakdown-slide";

function normalizeLiveDisplayActor(actor) {
  if (actor && typeof actor === "object") {
    const orgSession = actor.churchOrgSession || {};
    const account = orgSession.account || actor.account || actor.organization || {};

    return {
      userId: actor.id || "",
      accountId: account.id || orgSession.accountId || "",
      sessionToken: orgSession.sessionToken || orgSession.session_token || "",
      isChurchOrgUser: Boolean(actor.isChurchOrgUser || orgSession.sessionToken || orgSession.session_token),
    };
  }

  return {
    userId: actor || "",
    accountId: "",
    sessionToken: "",
    isChurchOrgUser: false,
  };
}
const ATTENDANCE_SLIDE_ID = "attendance-summary-slide";
const MEMBERS_FAMILIES_SLIDE_ID = "members-families-slide";
const SERVICE_TIMES_SLIDE_ID = "service-times-slide";
const MENS_DUTIES_SLIDE_ID = "mens-duties-slide";
const PREACHER_SLIDE_ID = "preacher-profile-slide";
const SURROUNDING_EVENTS_SLIDE_ID = "surrounding-events-slide";
const DEFAULT_SLEEP_SCHEDULE = {
  enabled: false,
  days: [],
  startTime: "22:00",
  endTime: "06:00",
};
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function normalizeLiveDisplayControlCode(publicCode = "") {
  return String(publicCode || "").trim().toUpperCase();
}

function getLiveDisplayControlChannelName(publicCode = "") {
  const code = normalizeLiveDisplayControlCode(publicCode);
  return code ? `${LIVE_DISPLAY_CONTROL_CHANNEL_PREFIX}:${code}` : "";
}

function getFamilyRoleRank(role = "") {
  const normalizedRole = String(role || "").trim().toLowerCase();
  if (normalizedRole === "father" || normalizedRole === "dad") return 1;
  if (normalizedRole === "mother" || normalizedRole === "mom") return 2;
  if (normalizedRole === "child" || normalizedRole === "kid" || normalizedRole === "kids") return 3;
  return 4;
}

function sortFamilyMembers(members = []) {
  return [...members].sort((a, b) => {
    const roleRank = getFamilyRoleRank(a.role) - getFamilyRoleRank(b.role);
    if (roleRank !== 0) return roleRank;

    if (getFamilyRoleRank(a.role) === 3) {
      const aBirthdate = a.birthdate || "9999-12-31";
      const bBirthdate = b.birthdate || "9999-12-31";
      if (aBirthdate !== bBirthdate) return aBirthdate.localeCompare(bBirthdate);
    }

    const aName = String(a.title || a.name || "").trim();
    const bName = String(b.title || b.name || "").trim();
    return aName.localeCompare(bName);
  });
}

function getFamilyImageCacheKey(accountId = "", path = "") {
  return `${accountId || "local"}::${path}`;
}

function readFamilyImageSignedUrlCache(accountId = "", paths = []) {
  if (typeof window === "undefined" || paths.length === 0) {
    return new Map();
  }

  try {
    const raw = window.localStorage.getItem(FAMILY_IMAGE_SIGNED_URL_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    const signedUrlMap = new Map();

    paths.forEach((path) => {
      const item = parsed[getFamilyImageCacheKey(accountId, path)];
      const cachedAt = Number(item?.cachedAt || 0);

      if (item?.signedUrl && cachedAt > 0 && now - cachedAt < FAMILY_IMAGE_SIGNED_URL_CACHE_TTL_MS) {
        signedUrlMap.set(path, item.signedUrl);
      }
    });

    return signedUrlMap;
  } catch (_error) {
    return new Map();
  }
}

function writeFamilyImageSignedUrlCache(accountId = "", signedEntries = []) {
  if (typeof window === "undefined" || signedEntries.length === 0) {
    return;
  }

  try {
    const raw = window.localStorage.getItem(FAMILY_IMAGE_SIGNED_URL_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const now = Date.now();

    signedEntries.forEach((entry) => {
      if (entry?.path && entry?.signedUrl) {
        parsed[getFamilyImageCacheKey(accountId, entry.path)] = {
          cachedAt: now,
          signedUrl: entry.signedUrl,
        };
      }
    });

    window.localStorage.setItem(FAMILY_IMAGE_SIGNED_URL_CACHE_KEY, JSON.stringify(parsed));
  } catch (_error) {
    // The display can still load; it will just request fresh signed URLs next time.
  }
}

function normalizeSleepSchedule(schedule = {}) {
  const rawDays = Array.isArray(schedule?.days) ? schedule.days : [];
  const days = rawDays
    .map((day) => String(day || "").trim().toLowerCase())
    .filter((day, index, list) => WEEKDAY_KEYS.includes(day) && list.indexOf(day) === index);
  const startTime = /^\d{2}:\d{2}$/.test(String(schedule?.startTime || ""))
    ? schedule.startTime
    : DEFAULT_SLEEP_SCHEDULE.startTime;
  const endTime = /^\d{2}:\d{2}$/.test(String(schedule?.endTime || ""))
    ? schedule.endTime
    : DEFAULT_SLEEP_SCHEDULE.endTime;

  return {
    enabled: schedule?.enabled === true,
    days,
    startTime,
    endTime,
  };
}

function normalizeScreenGroup(value = "") {
  const group = String(value || "").trim().toLowerCase();
  return SCREEN_GROUPS.has(group) ? group : DEFAULT_SCREEN_GROUP;
}

function getDefaultScreenNamePrefix(screenGroup = DEFAULT_SCREEN_GROUP) {
  return normalizeScreenGroup(screenGroup) === "interactive" ? "foyer" : "screen";
}

function isGenericScreenName(screenName = "") {
  const normalizedName = String(screenName || "").trim().toLowerCase();
  return [
    "",
    "live screen",
    "interactive display",
    "interactive screen",
    "service screen",
    "foyer screen",
  ].includes(normalizedName);
}

function buildUniqueScreenName(screenGroup = DEFAULT_SCREEN_GROUP, screens = [], excludeScreenCode = "") {
  const prefix = getDefaultScreenNamePrefix(screenGroup);
  const usedNames = new Set(
    screens
      .filter((screen) => !excludeScreenCode || screen?.screen_code !== excludeScreenCode)
      .map((screen) => String(screen?.screen_name || "").trim().toLowerCase())
      .filter(Boolean)
  );
  const seed = `${excludeScreenCode || Date.now()}-${Math.random().toString(36).slice(2)}`.toUpperCase();
  const characters = seed.replace(/[^A-Z0-9]/g, "") || "OIKOS";

  for (let offset = 0; offset < characters.length; offset += 1) {
    const suffix = `${characters.slice(offset)}${characters.slice(0, offset)}`.slice(0, 5).padEnd(5, "0");
    const candidate = `${prefix}-${suffix}`;

    if (!usedNames.has(candidate.toLowerCase())) {
      return candidate;
    }
  }

  return `${prefix}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
}

async function createUniqueScreenName(displayId, screenGroup, screenCode, requestedName = "") {
  if (!isGenericScreenName(requestedName)) {
    return String(requestedName || "").trim();
  }

  const { data, error } = await supabase
    .from(LIVE_SCREENS_TABLE)
    .select("screen_code, screen_name")
    .eq("display_id", displayId);

  if (error) {
    if (!isMissingRelationError(error)) {
      console.error("Live display screen name load error:", error);
    }
    return getDefaultScreenNamePrefix(screenGroup);
  }

  return buildUniqueScreenName(screenGroup, data || [], screenCode);
}

function normalizeInteractiveConfig(config = {}) {
  const apps = {
    ...DEFAULT_INTERACTIVE_CONFIG.apps,
    ...(config?.apps || {}),
  };

  Object.keys(apps).forEach((key) => {
    apps[key] = apps[key] !== false;
  });

  return {
    ...DEFAULT_INTERACTIVE_CONFIG,
    ...(config && typeof config === "object" ? config : {}),
    apps,
  };
}

function formatCurrency(value = 0) {
  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: "currency",
  }).format(Number(value) || 0);
}

function formatDate(value = "") {
  if (!value) return "";

  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch (_error) {
    return value;
  }
}

function normalizeLoopDuration(value) {
  return Math.min(Math.max(Number(value) || DEFAULT_LOOP_INTERVAL_SECONDS, 3), 120);
}

function buildCurrentHymnsLoopItem(serviceItems = [], existingItem = null) {
  const hymnItems = serviceItems.filter((item) => item?.itemType === "hymn");
  const entries = hymnItems.map((item, index) => {
    const songNumber = item?.payload?.songNumber || item?.songNumber || "";
    const label = item?.payload?.slotLabel || `Hymn ${index + 1}`;
    const hymnTitle = item?.payload?.hymnTitle || item?.title || "";

    return {
      id: item?.id || `hymn-${index + 1}`,
      label,
      title: songNumber ? `#${songNumber}` : hymnTitle || "Selected hymn",
      body: hymnTitle,
    };
  });

  return {
    id: CURRENT_HYMNS_SLIDE_ID,
    type: "current-hymns",
    title: "Today's Hymns",
    subtitle: "",
    body: entries.length > 0
      ? entries.map((entry) => [entry.label, entry.title, entry.body].filter(Boolean).join("\n")).join("\n\n")
      : "No hymns have been selected yet.",
    tone: "hymns",
    isVisible: existingItem?.isVisible !== false,
    durationSeconds: normalizeLoopDuration(existingItem?.durationSeconds),
    entries,
  };
}

function normalizeLoopServiceItem(item = {}) {
  const payload = item.payload || {};

  return {
    ...item,
    payload,
    slides: Array.isArray(payload.slides) ? payload.slides : [],
    itemType: item.itemType || item.type || payload.type || "item",
    sourceApp: item.sourceApp || item.source_app || "service",
  };
}

async function loadPreServiceHymnItems(userId, fallbackItems = []) {
  try {
    const weeklyPlan = await loadUpcomingWeeklyHymnPlan(userId);
    const weeklyItems = Array.isArray(weeklyPlan?.items)
      ? weeklyPlan.items.map((item) => normalizeLoopServiceItem(item))
      : [];

    return weeklyItems.length > 0 ? weeklyItems : fallbackItems;
  } catch (error) {
    console.error("Pre-service hymn list load error:", error);
    return fallbackItems;
  }
}

async function loadPublicPreServiceHymnItems(publicCode, fallbackItems = []) {
  try {
    const weeklyPlan = await loadUpcomingWeeklyHymnPlanForDisplay(publicCode);
    const weeklyItems = Array.isArray(weeklyPlan?.items)
      ? weeklyPlan.items.map((item) => normalizeLoopServiceItem(item))
      : [];

    return weeklyItems.length > 0 ? weeklyItems : fallbackItems;
  } catch (error) {
    console.error("Public pre-service hymn list load error:", error);
    return fallbackItems;
  }
}

function getOfferingRecordDate(record = {}) {
  return record.collection_date || record.collectionDate || "";
}

function getOfferingRecordTotal(record = {}) {
  return (
    Number(record.cash_total ?? record.cashTotal ?? 0) +
    Number(record.check_total ?? record.checkTotal ?? 0) +
    Number(record.online_total ?? record.onlineTotal ?? 0)
  );
}

function normalizeOfferingRecords(tithingRecord = null) {
  const records = Array.isArray(tithingRecord)
    ? tithingRecord
    : Array.isArray(tithingRecord?.records)
      ? tithingRecord.records
      : tithingRecord
        ? [tithingRecord]
        : [];

  return records
    .filter(Boolean)
    .slice()
    .sort((a, b) => String(getOfferingRecordDate(b)).localeCompare(String(getOfferingRecordDate(a))))
    .slice(0, 2);
}

function buildTithingBreakdownLoopItem(tithingRecord = null, existingItem = null) {
  const records = normalizeOfferingRecords(tithingRecord);

  if (records.length === 0) {
    return null;
  }

  const entries = records.map((record, index) => {
    const dateLabel = formatDate(getOfferingRecordDate(record));

    return {
      id: record.id || `offering-week-${index + 1}`,
      label: index === 0 ? "Most Recent" : "Previous Week",
      title: formatCurrency(getOfferingRecordTotal(record)),
      body: dateLabel || "Offering week",
    };
  });

  return {
    id: TITHING_BREAKDOWN_SLIDE_ID,
    type: "offerings",
    title: "Offerings",
    subtitle: records.length > 1 ? "Last 2 weeks" : "Most recent week",
    body: entries
      .map((entry) => [entry.label, entry.title, entry.body].filter(Boolean).join("\n"))
      .join("\n\n"),
    tone: "offerings",
    isVisible: existingItem?.isVisible !== false,
    durationSeconds: normalizeLoopDuration(existingItem?.durationSeconds),
    entries,
  };
}

function getAttendanceRecordDate(record = {}) {
  return record.attendance_date || record.attendanceDate || "";
}

function getAttendanceRecordServiceCount(record = {}) {
  return Number(record.worship_count ?? record.worshipCount ?? 0) || 0;
}

function getAttendanceRecordVisitorCount(record = {}) {
  return Number(record.visitor_count ?? record.visitorCount ?? 0) || 0;
}

function buildAttendanceLoopItem(attendanceRecord = null, existingItem = null) {
  if (!attendanceRecord) {
    return null;
  }

  const serviceCount = getAttendanceRecordServiceCount(attendanceRecord);
  const visitorCount = getAttendanceRecordVisitorCount(attendanceRecord);
  const dateLabel = formatDate(getAttendanceRecordDate(attendanceRecord));

  return {
    id: ATTENDANCE_SLIDE_ID,
    type: "attendance",
    title: "Attendance",
    subtitle: dateLabel ? `Most recent service • ${dateLabel}` : "Most recent service",
    body: `Service: ${serviceCount}\nVisitors: ${visitorCount}`,
    tone: "attendance",
    isVisible: existingItem?.isVisible !== false,
    durationSeconds: normalizeLoopDuration(existingItem?.durationSeconds),
    entries: [
      { id: "service", label: "Service", title: String(serviceCount), body: "" },
      { id: "visitors", label: "Visitors", title: String(visitorCount), body: "" },
    ],
  };
}

function formatLoopServiceTime(time = "") {
  const [hourValue, minuteValue] = String(time || "").split(":");
  const hour = Number(hourValue);
  const minute = Number(minuteValue);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return "";
  }

  return new Date(2000, 0, 1, hour, minute).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function buildServiceTimesLoopItem(serviceTimes = [], existingItem = null) {
  const entries = (Array.isArray(serviceTimes) ? serviceTimes : [])
    .filter((item) => item?.name || item?.time)
    .map((item, index) => ({
      id: item.id || `service-time-${index + 1}`,
      label: "Service Time",
      title: item.name || "Service",
      subtitle: formatLoopServiceTime(item.time),
      body: "",
    }));

  return {
    id: SERVICE_TIMES_SLIDE_ID,
    type: "service-times",
    title: "Service Times",
    subtitle: "",
    body: entries.length > 0
      ? entries.map((entry) => [entry.title, entry.subtitle].filter(Boolean).join(" - ")).join("\n")
      : "Add service times in Church Management to show them here.",
    tone: "service times",
    isVisible: existingItem?.isVisible !== false,
    durationSeconds: normalizeLoopDuration(existingItem?.durationSeconds || 8),
    showLogo: false,
    entries,
  };
}

function normalizePreacherProfile(integrations = {}) {
  const source = integrations?.church?.preacher || integrations?.preacher || {};

  return {
    name: String(source?.name || "").trim(),
    title: String(source?.title || "Preacher").trim() || "Preacher",
    email: String(source?.email || "").trim(),
    phone: String(source?.phone || "").trim(),
    bio: String(source?.bio || "").trim(),
    imageUrl: String(source?.imageUrl || "").trim(),
    imagePath: String(source?.imagePath || "").trim(),
  };
}

function buildPreacherLoopItem(integrations = {}, existingItem = null) {
  const preacher = normalizePreacherProfile(integrations);
  const hasProfile = Boolean(
    preacher.name ||
    preacher.bio ||
    preacher.imageUrl ||
    preacher.email ||
    preacher.phone
  );

  if (!hasProfile) {
    return null;
  }

  const contactLines = [preacher.email, preacher.phone].filter(Boolean);
  const bodyLines = [preacher.bio, ...contactLines].filter(Boolean);

  return {
    id: PREACHER_SLIDE_ID,
    type: "preacher",
    title: preacher.name || "Our Preacher",
    subtitle: preacher.title,
    body: bodyLines.join("\n"),
    tone: "preacher",
    isVisible: existingItem?.isVisible !== false,
    durationSeconds: normalizeLoopDuration(existingItem?.durationSeconds || 8),
    showLogo: false,
    imageUrl: preacher.imageUrl,
    imagePath: preacher.imagePath,
    entries: [
      {
        id: "preacher-profile",
        label: preacher.title,
        title: preacher.name || "Our Preacher",
        subtitle: contactLines.join(" • "),
        body: preacher.bio,
        imageUrl: preacher.imageUrl,
      },
    ],
    preacher,
  };
}

function getNextSundayDateString(fromDate = new Date()) {
  const date = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  const daysUntilSunday = (7 - date.getDay()) % 7;
  date.setDate(date.getDate() + daysUntilSunday);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatMensDutiesSunday(value = "") {
  if (!value) return "Upcoming Sunday";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "Upcoming Sunday";
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function buildMensDutiesLoopItem(duties = [], existingItem = null) {
  const nextSunday = getNextSundayDateString();
  const liveDuties = (Array.isArray(duties) ? duties : [])
    .filter((item) => item?.showOnLive !== false)
    .filter((item) => String(item?.dutyDate || item?.duty_date || "") >= nextSunday);
  const targetDate = liveDuties.find((item) => item?.dutyDate || item?.duty_date)?.dutyDate || nextSunday;
  const entries = liveDuties
    .filter((item) => (item.dutyDate || item.duty_date) === targetDate)
    .slice(0, 8)
    .map((item, index) => ({
      id: item.id || `mens-duty-${index + 1}`,
      label: item.dutyName || item.duty_name || "Duty",
      title: item.dutyName || item.duty_name || "Duty",
      subtitle: item.assigneeName || item.assignee_name || "Open",
      body: item.note || "",
    }));

  return {
    id: MENS_DUTIES_SLIDE_ID,
    type: "mens-duties",
    title: "Mens Duties",
    subtitle: formatMensDutiesSunday(targetDate),
    body: entries.length > 0
      ? entries.map((entry) => `${entry.title}: ${entry.subtitle}`).join("\n")
      : "No duties have been assigned for the upcoming Sunday yet.",
    tone: "duties",
    isVisible: existingItem?.isVisible !== false,
    durationSeconds: normalizeLoopDuration(existingItem?.durationSeconds || 8),
    showLogo: false,
    entries,
  };
}

function readLocalDirectoryEntries() {
  if (typeof window === "undefined") {
    return { entries: [], memberEntries: [] };
  }

  try {
    const raw = window.localStorage.getItem("oikos.church.management");
    const parsed = raw ? JSON.parse(raw) : null;
    const families = Array.isArray(parsed?.families) ? parsed.families : [];
    const memberEntries = [];

    const entries = families
      .map((family) => {
        const familyId = family.id || `family-${Math.random().toString(36).slice(2, 8)}`;
        const familyName = family.familyName || family.family_name || "Church family";
        const address = family.address || "";
        const members = Array.isArray(family.members) ? family.members : [];

        members.forEach((member) => {
          const firstName = member.firstName || member.first_name || "";
          const lastName = member.lastName || member.last_name || "";
          const name = [firstName, lastName].filter(Boolean).join(" ").trim() || "Church member";

          memberEntries.push({
            id: member.id || `${familyId}-${name}`,
            familyId,
            familyName,
            title: name,
            phone: member.phone || "",
            email: member.email || "",
            address,
            imageUrl: member.imageUrl || member.image_url || "",
            imagePath: member.imagePath || member.image_path || "",
          });
        });

        return {
          id: familyId,
          title: familyName,
          address,
          body: address,
          memberCount: members.length,
          imageUrl: family.imageUrl || family.image_url || "",
          imagePath: family.imagePath || family.image_path || "",
        };
      });

    return { entries, memberEntries };
  } catch (_error) {
    return { entries: [], memberEntries: [] };
  }
}

async function createManagementSignedUrlMap(accountId = "", paths = []) {
  const uniquePaths = Array.from(new Set(paths.filter(Boolean)));

  if (uniquePaths.length === 0) {
    return new Map();
  }

  const signedUrlMap = readFamilyImageSignedUrlCache(accountId, uniquePaths);
  const pathsToSign = uniquePaths.filter((path) => !signedUrlMap.has(path));

  if (pathsToSign.length > 0) {
    const signedResult = await supabase.storage
      .from(MANAGEMENT_MEDIA_BUCKET)
      .createSignedUrls(pathsToSign, 60 * 60 * 24 * 7);

    if (!signedResult.error) {
      const cacheEntries = [];

      (signedResult.data || []).forEach((item, index) => {
        if (item?.signedUrl) {
          const path = pathsToSign[index];
          signedUrlMap.set(path, item.signedUrl);
          cacheEntries.push({ path, signedUrl: item.signedUrl });
        }
      });

      writeFamilyImageSignedUrlCache(accountId, cacheEntries);
    } else {
      console.error("Live display member directory image signing error:", signedResult.error);
    }
  }

  return signedUrlMap;
}

async function loadFamilyImageEntries(accountId = "") {
  if (!accountId) {
    return readLocalDirectoryEntries().entries;
  }

  let { data, error } = await supabase
    .from(FAMILIES_TABLE)
    .select("id, family_name, address, anniversary, image_url, image_path")
    .eq("account_id", accountId)
    .order("family_name", { ascending: true });

  if (error && isMissingColumnError(error, "anniversary")) {
    const fallbackResult = await supabase
      .from(FAMILIES_TABLE)
      .select("id, family_name, address, image_url, image_path")
      .eq("account_id", accountId)
      .order("family_name", { ascending: true });

    data = fallbackResult.data;
    error = fallbackResult.error;
  }

  if (error) {
    if (!isMissingRelationError(error)) {
      console.error("Live display family directory load error:", error);
    }

    return readLocalDirectoryEntries().entries;
  }

  const families = (data || []).slice(0, FAMILY_IMAGE_DISPLAY_LIMIT);
  const signedUrlMap = await createManagementSignedUrlMap(
    accountId,
    families.map((family) => family.image_path)
  );

  return families
    .map((family) => ({
        id: family.id || `family-${Math.random().toString(36).slice(2, 8)}`,
        title: family.familyName || family.family_name || "Church family",
        address: family.address || "",
        anniversary: family.anniversary || "",
        body: family.address || "",
        imageUrl: family.imageUrl || family.image_url || "",
        imagePath: family.imagePath || family.image_path || "",
      }))
    .map((family) => ({
      ...family,
      imageUrl: signedUrlMap.get(family.imagePath) || family.imageUrl || "",
    }));
}

async function loadMemberDirectoryEntries(accountId = "", familyEntries = []) {
  if (!accountId) {
    return readLocalDirectoryEntries().memberEntries;
  }

  const { data, error } = await supabase
    .from(MEMBERS_TABLE)
    .select("id, family_id, first_name, last_name, phone, email, image_url, image_path, birthdate, member_role, is_active")
    .eq("account_id", accountId)
    .eq("is_active", true)
    .order("last_name", { ascending: true })
    .order("first_name", { ascending: true });

  if (error) {
    if (!isMissingRelationError(error)) {
      console.error("Live display member directory load error:", error);
    }

    return readLocalDirectoryEntries().memberEntries;
  }

  const familyById = new Map(
    familyEntries.map((family) => [family.id, family])
  );
  const members = (data || []).slice(0, MEMBER_DIRECTORY_DISPLAY_LIMIT);
  const signedUrlMap = await createManagementSignedUrlMap(
    accountId,
    members.map((member) => member.image_path)
  );

  return members.map((member) => {
    const family = familyById.get(member.family_id) || {};
    const title = [member.first_name, member.last_name].filter(Boolean).join(" ").trim() || "Church member";

    return {
      id: member.id,
      familyId: member.family_id || "",
      familyName: family.title || "",
      title,
      phone: member.phone || "",
      email: member.email || "",
      address: family.address || "",
      familyAnniversary: family.anniversary || "",
      imageUrl: signedUrlMap.get(member.image_path) || member.image_url || "",
      imagePath: member.image_path || "",
      birthdate: member.birthdate || "",
      role: member.member_role || "Member",
    };
  });
}

function normalizeMemberDirectoryRpcRows(rows = []) {
  const familyMap = new Map();
  const memberEntries = [];

  (rows || []).forEach((row) => {
    const familyId = row.family_id || "";
    if (!familyId) return;

    if (!familyMap.has(familyId)) {
      familyMap.set(familyId, {
        id: familyId,
        title: row.family_name || "Church family",
        address: row.family_address || "",
        anniversary: row.family_anniversary || "",
        body: row.family_address || "",
        imageUrl: row.family_image_url || "",
        imagePath: row.family_image_path || "",
        members: [],
      });
    }

    if (!row.member_id) return;

    const title = [row.member_first_name, row.member_last_name].filter(Boolean).join(" ").trim() || "Church member";
    const member = {
      id: row.member_id,
      familyId,
      familyName: row.family_name || "",
      title,
      phone: row.member_phone || "",
      email: row.member_email || "",
      address: row.family_address || "",
      familyAnniversary: row.family_anniversary || "",
      imageUrl: row.member_image_url || "",
      imagePath: row.member_image_path || "",
      birthdate: row.member_birthdate || "",
      role: row.member_role || "Member",
    };

    familyMap.get(familyId).members.push(member);
    memberEntries.push(member);
  });

  return {
    entries: Array.from(familyMap.values()).map((family) => ({
      ...family,
      members: sortFamilyMembers(family.members),
    })),
    memberEntries: sortFamilyMembers(memberEntries),
  };
}

async function hydrateDirectoryFamilyAnniversaries(accountId = "", directory = {}) {
  const entries = Array.isArray(directory?.entries) ? directory.entries : [];
  const memberEntries = Array.isArray(directory?.memberEntries) ? directory.memberEntries : [];

  if (!accountId || entries.length === 0) {
    return {
      entries,
      memberEntries,
    };
  }

  const missingAnniversary = entries.some((family) => !family?.anniversary);
  if (!missingAnniversary) {
    return {
      ...directory,
      entries,
      memberEntries,
    };
  }

  const { data, error } = await supabase
    .from(FAMILIES_TABLE)
    .select("id, anniversary")
    .eq("account_id", accountId);

  if (error) {
    if (!isMissingRelationError(error) && !isMissingColumnError(error, "anniversary")) {
      console.error("Live display family anniversary hydrate error:", error);
    }

    return {
      ...directory,
      entries,
      memberEntries,
    };
  }

  const anniversaryByFamilyId = new Map(
    (data || []).map((family) => [family.id, family.anniversary || ""])
  );

  return {
    ...directory,
    entries: entries.map((family) => ({
      ...family,
      anniversary: family.anniversary || anniversaryByFamilyId.get(family.id) || "",
    })),
    memberEntries: memberEntries.map((member) => ({
      ...member,
      familyAnniversary: member.familyAnniversary || anniversaryByFamilyId.get(member.familyId) || "",
    })),
  };
}

async function loadPublicMembersDirectoryEntries(accountId = "", publicCode = "") {
  const safePublicCode = String(publicCode || "").trim();
  if (!safePublicCode) return null;

  const { data, error } = await supabase.rpc("church_live_display_member_directory", {
    p_public_code: safePublicCode,
  });

  if (error) {
    if (!isMissingRelationError(error)) {
      console.error("Live display member directory RPC load error:", error);
    }

    return null;
  }

  const directory = await hydrateDirectoryFamilyAnniversaries(
    accountId,
    normalizeMemberDirectoryRpcRows(data || [])
  );
  const imagePaths = [
    ...directory.entries.map((family) => family.imagePath),
    ...directory.memberEntries.map((member) => member.imagePath),
  ];
  const signedUrlMap = await createManagementSignedUrlMap(accountId, imagePaths);
  const memberEntries = directory.memberEntries.map((member) => ({
    ...member,
    imageUrl: signedUrlMap.get(member.imagePath) || member.imageUrl || "",
  }));
  const membersByFamilyId = new Map();

  memberEntries.forEach((member) => {
    const familyMembers = membersByFamilyId.get(member.familyId) || [];
    familyMembers.push(member);
    membersByFamilyId.set(member.familyId, sortFamilyMembers(familyMembers));
  });

  return {
    entries: directory.entries.map((family) => ({
      ...family,
      imageUrl: signedUrlMap.get(family.imagePath) || family.imageUrl || "",
      members: membersByFamilyId.get(family.id) || [],
    })),
    memberEntries,
  };
}

async function loadMembersDirectoryEntries(accountId = "", options = {}) {
  const publicDirectory = await loadPublicMembersDirectoryEntries(accountId, options.publicCode);
  if (publicDirectory?.entries?.length || publicDirectory?.memberEntries?.length) {
    return publicDirectory;
  }

  const entries = await loadFamilyImageEntries(accountId);
  const memberEntries = await loadMemberDirectoryEntries(accountId, entries);
  const membersByFamilyId = new Map();

  memberEntries.forEach((member) => {
    const key = member.familyId || member.family_id || "";
    if (!key) return;

    const familyMembers = membersByFamilyId.get(key) || [];
    familyMembers.push(member);
    membersByFamilyId.set(key, sortFamilyMembers(familyMembers));
  });

  return {
    entries: entries.map((family) => ({
      ...family,
      members: sortFamilyMembers(membersByFamilyId.get(family.id) || []),
    })),
    memberEntries: sortFamilyMembers(memberEntries),
  };
}

async function buildMembersFamiliesLoopItem(accountId = "", existingItem = null, options = {}) {
  const loadedDirectory = await loadMembersDirectoryEntries(accountId, options);
  const fallbackEntries = Array.isArray(existingItem?.entries)
    ? existingItem.entries
    : [];
  const fallbackMemberEntries = Array.isArray(existingItem?.memberEntries)
    ? existingItem.memberEntries
    : [];
  const entries = loadedDirectory.entries.length > 0 ? loadedDirectory.entries : fallbackEntries;
  const memberEntries = loadedDirectory.memberEntries.length > 0 ? loadedDirectory.memberEntries : fallbackMemberEntries;

  return {
    id: MEMBERS_FAMILIES_SLIDE_ID,
    type: "members",
    title: "Families",
    subtitle: "",
    body: entries.length > 0 || memberEntries.length > 0
      ? `${entries.length} ${entries.length === 1 ? "family" : "families"} and ${memberEntries.length} ${memberEntries.length === 1 ? "member" : "members"} ready.`
      : "Add families and members in Church Management to fill this directory.",
    tone: "members",
    isVisible: existingItem?.isVisible !== false,
    durationSeconds: normalizeLoopDuration(existingItem?.durationSeconds || 10),
    showLogo: false,
    entries,
    memberEntries,
  };
}

function mergeGeneratedLoopItem(loopItems = [], nextItem = null, supportedIds = []) {
  const nextLoopItems = Array.isArray(loopItems) ? [...loopItems] : [];
  const existingIndex = nextLoopItems.findIndex((item) => supportedIds.includes(item?.id));
  const existingItem = existingIndex >= 0 ? nextLoopItems[existingIndex] : null;

  if (!nextItem) {
    return nextLoopItems.filter((item) => !supportedIds.includes(item?.id));
  }

  if (existingIndex >= 0) {
    nextLoopItems.splice(existingIndex, 1, nextItem);
    return nextLoopItems;
  }

  return [...nextLoopItems, nextItem];
}

function mergeGeneratedLoopItems(loopItems = [], options = {}) {
  const {
    serviceItems = [],
    tithingRecord,
    attendanceRecord,
    membersItem,
    serviceTimesItem,
    mensDutiesItem,
    preacherItem,
    surroundingEventsItem,
  } = options;
  const withHymns = mergeGeneratedLoopItem(
    loopItems,
    buildCurrentHymnsLoopItem(
      serviceItems,
      (loopItems || []).find((item) =>
        [CURRENT_HYMNS_SLIDE_ID, LEGACY_TODAY_SONGS_SLIDE_ID].includes(item?.id)
      )
    ),
    [CURRENT_HYMNS_SLIDE_ID, LEGACY_TODAY_SONGS_SLIDE_ID]
  );

  const shouldMergeMembers = Object.prototype.hasOwnProperty.call(options, "membersItem");
  const shouldMergeServiceTimes = Object.prototype.hasOwnProperty.call(options, "serviceTimesItem");
  const shouldMergeMensDuties = Object.prototype.hasOwnProperty.call(options, "mensDutiesItem");
  const shouldMergePreacher = Object.prototype.hasOwnProperty.call(options, "preacherItem");
  const shouldMergeSurroundingEvents = Object.prototype.hasOwnProperty.call(options, "surroundingEventsItem");
  const shouldMergeAttendance = Object.prototype.hasOwnProperty.call(options, "attendanceRecord");
  const withServiceTimes = shouldMergeServiceTimes
    ? mergeGeneratedLoopItem(
        withHymns,
        serviceTimesItem,
        [SERVICE_TIMES_SLIDE_ID]
      )
    : withHymns;
  const withPreacher = shouldMergePreacher
    ? mergeGeneratedLoopItem(
        withServiceTimes,
        preacherItem,
        [PREACHER_SLIDE_ID]
      )
    : withServiceTimes;
  const withMensDuties = shouldMergeMensDuties
    ? mergeGeneratedLoopItem(
        withPreacher,
        mensDutiesItem,
        [MENS_DUTIES_SLIDE_ID]
      )
    : withPreacher;
  const withSurroundingEvents = shouldMergeSurroundingEvents
    ? mergeGeneratedLoopItem(
        withMensDuties,
        surroundingEventsItem,
        [SURROUNDING_EVENTS_SLIDE_ID]
      )
    : withMensDuties;

  const withAttendance = shouldMergeAttendance
    ? mergeGeneratedLoopItem(
        withSurroundingEvents,
        buildAttendanceLoopItem(
          attendanceRecord,
          (loopItems || []).find((item) => item?.id === ATTENDANCE_SLIDE_ID)
        ),
        [ATTENDANCE_SLIDE_ID]
      )
    : withSurroundingEvents;
  const withTithing = typeof tithingRecord === "undefined"
    ? withAttendance
    : mergeGeneratedLoopItem(
        withAttendance,
        buildTithingBreakdownLoopItem(
          tithingRecord,
          (loopItems || []).find((item) => item?.id === TITHING_BREAKDOWN_SLIDE_ID)
        ),
        [TITHING_BREAKDOWN_SLIDE_ID]
      );

  return shouldMergeMembers
    ? mergeGeneratedLoopItem(
        withTithing,
        membersItem,
        [MEMBERS_FAMILIES_SLIDE_ID]
      )
    : withTithing;
}

function mergeLoopSettings(savedItems = [], nextItems = []) {
  const nextMap = new Map((nextItems || []).map((item) => [item.id, item]));
  const seenIds = new Set();
  const orderedItems = [];

  (savedItems || []).forEach((savedItem) => {
    if (!savedItem?.id) return;
    const generatedItem = nextMap.get(savedItem.id);
    const baseItem = generatedItem || savedItem;

    orderedItems.push({
      ...baseItem,
      isVisible: savedItem.isVisible !== false,
      durationSeconds: normalizeLoopDuration(savedItem.durationSeconds ?? baseItem.durationSeconds),
    });
    seenIds.add(savedItem.id);
  });

  (nextItems || []).forEach((item) => {
    if (!item?.id || seenIds.has(item.id)) return;
    orderedItems.push({
      ...item,
      isVisible: item.isVisible !== false,
      durationSeconds: normalizeLoopDuration(item.durationSeconds),
    });
  });

  return orderedItems;
}

function isMissingRelationError(error) {
  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    (typeof error?.message === "string" &&
      error.message.toLowerCase().includes("does not exist"))
  );
}

function isMissingLiveDisplayColumnError(error, columnName) {
  return (
    error?.code === "PGRST204" &&
    typeof error?.message === "string" &&
    error.message.includes(columnName)
  );
}

function isMissingColumnError(error, columnName) {
  return (
    error?.code === "42703" ||
    isMissingLiveDisplayColumnError(error, columnName) ||
    (
      typeof error?.message === "string" &&
      error.message.toLowerCase().includes(columnName.toLowerCase()) &&
      error.message.toLowerCase().includes("column")
    )
  );
}

function isActiveDisplayUniqueError(error) {
  const message = String(error?.message || "");
  return (
    error?.code === "23505" &&
    message.includes("church_live_displays_user_mode_active_idx")
  );
}

function getStorageKey(userId) {
  return `${STORAGE_KEY_PREFIX}.${userId || "guest"}`;
}

function getPublicStorageKey(code) {
  return `${STORAGE_KEY_PREFIX}.public.${code}`;
}

function createCode() {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

function createSessionName() {
  const now = new Date();
  return `Sunday Display ${now.toLocaleDateString()}`;
}

function getDeviceToken(displayId) {
  if (typeof window === "undefined") {
    return "device-server";
  }

  const key = `${SCREEN_DEVICE_PREFIX}.${displayId}`;
  const existing = window.localStorage.getItem(key);

  if (existing) {
    return existing;
  }

  const next = `device-${Math.random().toString(36).slice(2, 10)}`;
  window.localStorage.setItem(key, next);
  return next;
}

function getScreenCode(displayId, deviceToken, screenGroup = DEFAULT_SCREEN_GROUP) {
  const safeGroup = normalizeScreenGroup(screenGroup);
  return safeGroup === DEFAULT_SCREEN_GROUP
    ? `${displayId}-${deviceToken}`
    : `${displayId}-${safeGroup}-${deviceToken}`;
}

function getSourceId(item = {}) {
  return item.id || item.source_id || "";
}

function isQuickHymnSource(selectedServiceItemId = "") {
  return typeof selectedServiceItemId === "string" &&
    selectedServiceItemId.startsWith(QUICK_HYMN_SOURCE_PREFIX);
}

function isQuickLiveSource(selectedServiceItemId = "") {
  return (
    isQuickHymnSource(selectedServiceItemId) ||
    (typeof selectedServiceItemId === "string" &&
      selectedServiceItemId.startsWith(QUICK_SERMON_SOURCE_PREFIX))
  );
}

function buildLiveSlides(serviceItems = [], selectedServiceItemId = "") {
  if (isQuickHymnSource(selectedServiceItemId)) {
    return [];
  }

  if (!selectedServiceItemId || selectedServiceItemId === "all-service-items") {
    return buildSlidesFromServiceItems(serviceItems);
  }

  const selectedItem = serviceItems.find(
    (item) => getSourceId(item) === selectedServiceItemId
  );

  return selectedItem ? buildSlidesFromServiceItems([selectedItem]) : [];
}

function normalizeLiveSlides(savedSlides = [], serviceItems = [], selectedServiceItemId = "") {
  if (Array.isArray(savedSlides) && savedSlides.length > 0) {
    return savedSlides.map((slide, index) => ({
      id: slide.id || `live-slide-${index + 1}`,
      itemType: slide.itemType || "custom_slide",
      sourceType: slide.sourceType || slide.source_type || "",
      title: slide.title || "",
      subtitle: slide.subtitle || "",
      date: slide.date || "",
      reference: slide.reference || "",
      verseNumber: slide.verseNumber || "",
      text: slide.text || "",
      body: slide.body || "",
      bodyHtml: slide.bodyHtml || slide.body_html || "",
      songNumber: slide.songNumber || slide.song_number || "",
      imageUrl: slide.imageUrl || slide.image_url || "",
      imagePath: slide.imagePath || slide.image_path || "",
      storageBucket: slide.storageBucket || slide.storage_bucket || "global-hymn-files",
      imageFit: slide.imageFit || slide.image_fit || "contain",
      imagePositionX: Number.isFinite(Number(slide.imagePositionX ?? slide.image_position_x))
        ? Number(slide.imagePositionX ?? slide.image_position_x)
        : 50,
      imagePositionY: Number.isFinite(Number(slide.imagePositionY ?? slide.image_position_y))
        ? Number(slide.imagePositionY ?? slide.image_position_y)
        : 50,
      parentId: slide.parentId || "",
      isEndOfSong:
        typeof slide.isEndOfSong === "boolean"
          ? slide.isEndOfSong
          : typeof slide.is_end_of_song === "boolean"
            ? slide.is_end_of_song
            : false,
    }));
  }

  return buildLiveSlides(serviceItems, selectedServiceItemId);
}

function dedupeScreens(rows = []) {
  const map = new Map();

  rows.forEach((screen) => {
    const normalized = normalizeScreen(screen);
    const key =
      normalized.screenCode || normalized.id || `${normalized.deviceInfo}-${normalized.name}`;
    const current = map.get(key);

    if (!current) {
      map.set(key, normalized);
      return;
    }

    const currentTime = new Date(current.lastSeenAt || 0).getTime();
    const nextTime = new Date(normalized.lastSeenAt || 0).getTime();

    if (nextTime >= currentTime) {
      map.set(key, normalized);
    }
  });

  return Array.from(map.values()).sort(
    (a, b) => new Date(b.lastSeenAt || 0).getTime() - new Date(a.lastSeenAt || 0).getTime()
  );
}

function filterActiveScreens(rows = []) {
  const cutoff = Date.now() - ACTIVE_SCREEN_WINDOW_MS;

  return rows.filter((screen) => {
    const seenAt = new Date(screen.lastSeenAt || 0).getTime();
    return Number.isFinite(seenAt) && seenAt >= cutoff;
  });
}

function getManageableScreens(rows = []) {
  return dedupeScreens(rows);
}

export async function loadChurchLiveScreens(display) {
  if (!display?.id && !display?.publicCode) {
    return [];
  }

  const localPublicScreens = display?.publicCode
    ? loadLocalPublicDisplay(display.publicCode)?.screens || []
    : [];

  if (!display?.id || display.id === "local-live-display") {
    return getManageableScreens(localPublicScreens);
  }

  const { data, error } = await supabase
    .from(LIVE_SCREENS_TABLE)
    .select("*")
    .eq("display_id", display.id)
    .order("last_seen_at", { ascending: false });

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    return getManageableScreens(localPublicScreens);
  }

  return getManageableScreens([
    ...(Array.isArray(data) ? data : []),
    ...localPublicScreens,
  ]);
}

function normalizeLoopItems(
  items = [],
  serviceId = "current_service",
  serviceItems = [],
  branding = {}
) {
  const welcomeSlide = {
    id: "welcome-slide",
    title: "Welcome To",
    subtitle: branding.name || "Fallon Church of Christ",
    body: "We are glad you are here. Service will begin shortly.",
    tone: "welcome",
    type: "welcome",
    isVisible: true,
    showLogo: true,
    logoUrl: branding.logo_url || "",
  };

  if (Array.isArray(items) && items.length > 0) {
    const normalizedItems = items
      .filter(
        (item) => !(item?.title === LEGACY_LOOP_TITLE && item?.body === LEGACY_LOOP_BODY)
      )
      .map((item, index) => {
      const normalized = {
        id: item.id || `loop-${index + 1}`,
        title: item.title || "Church Update",
        subtitle: item.subtitle || "",
        body: item.body || "",
        entries: Array.isArray(item.entries) ? item.entries : [],
        memberEntries: Array.isArray(item.memberEntries)
          ? item.memberEntries
          : Array.isArray(item.member_entries)
            ? item.member_entries
            : [],
        durationSeconds: normalizeLoopDuration(item.durationSeconds),
        tone: item.tone || "welcome",
        type: item.type || "announcement",
        isVisible: item.isVisible !== false,
        showLogo: Boolean(item.showLogo),
        logoUrl: item.logoUrl || "",
        imageUrl: item.imageUrl || "",
        imagePath: item.imagePath || "",
        preacher: item.preacher && typeof item.preacher === "object" ? item.preacher : null,
      };

      if (normalized.id === "welcome-slide" || normalized.type === "welcome") {
        return {
          ...welcomeSlide,
          ...normalized,
          title: "Welcome To",
          subtitle: branding.name || normalized.subtitle || "Fallon Church of Christ",
          body: welcomeSlide.body,
          tone: "welcome",
          type: "welcome",
          showLogo: true,
          logoUrl: branding.logo_url || "",
        };
      }

      if (
        normalized.id === "announcements-slide" ||
        normalized.id === "events-slide" ||
        normalized.id === SURROUNDING_EVENTS_SLIDE_ID
      ) {
        return {
          ...normalized,
          subtitle: "",
        };
      }

      return normalized;
      });

    const hasWelcome = normalizedItems.some(
      (item) => item.id === "welcome-slide" || item.type === "welcome"
    );

    return hasWelcome ? normalizedItems : [welcomeSlide, ...normalizedItems];
  }

  return buildPreServiceLoopItems({
    churchName: branding.name || "Fallon Church of Christ",
    churchLogoUrl: branding.logo_url || "",
  });
}

function normalizeDisplay(row = {}, serviceItems = [], branding = {}, loopServiceItems = serviceItems) {
  const serviceId = row.service_id || getCurrentServiceId();
  const selectedServiceItemId =
    row.selected_service_item_id || "all-service-items";
  const slides = normalizeLiveSlides(row.live_slides, serviceItems, selectedServiceItemId);
  const currentSlideIndex = Math.min(
    Math.max(Number(row.current_slide_index) || 0, 0),
    Math.max(slides.length - 1, 0)
  );
  const organizationName = row.organization_name || branding.name || "";
  const organizationLogoUrl = row.organization_logo_url || branding.logo_url || "";
  const organizationLoginCode =
    row.organization_login_code ||
    branding.church_code ||
    branding.churchCode ||
    branding.invite_code ||
    branding.inviteCode ||
    branding.id ||
    "";
  const loopIntervalSeconds = Math.min(
    Math.max(Number(row.loop_interval_seconds) || DEFAULT_LOOP_INTERVAL_SECONDS, 3),
    60
  );
  const sleepSchedule = normalizeSleepSchedule(row.sleep_schedule || row.sleepSchedule);
  const interactiveConfig = normalizeInteractiveConfig(row.interactive_config || row.interactiveConfig);

  const normalizedLoopItems = normalizeLoopItems(row.pre_service_items, serviceId, serviceItems, branding);

  return {
    id: row.id || "local-live-display",
    accountId: row.account_id || branding.id || "",
    userId: row.user_id || row.userId || "",
    name: row.name || createSessionName(),
    mode: row.mode || "church",
    serviceId,
    publicCode: row.public_code || createCode(),
    publicUrl: `${window.location.origin}/live/${row.public_code || createCode()}`,
    state: row.display_state || "loop",
    isReady: Boolean(row.is_ready),
    isLive: Boolean(row.is_live),
    selectedServiceItemId,
    currentSlideIndex,
    createdAt: row.created_at || "",
    updatedAt: row.updated_at || "",
    loopIntervalSeconds,
    sleepSchedule,
    interactiveConfig,
    organizationName,
    organizationLogoUrl,
    organizationLoginCode,
    loopItems: loopServiceItems === null
      ? normalizedLoopItems
      : mergeGeneratedLoopItems(
          normalizedLoopItems,
          {
            serviceItems: loopServiceItems || serviceItems,
            tithingRecord: Object.prototype.hasOwnProperty.call(row, "tithing_record")
              ? row.tithing_record || null
              : undefined,
            attendanceRecord: Object.prototype.hasOwnProperty.call(row, "attendance_record")
              ? row.attendance_record || null
              : undefined,
          }
        ),
    serviceItems,
    serviceTimes: Array.isArray(row.service_times || row.serviceTimes)
      ? row.service_times || row.serviceTimes
      : [],
    mensDutyTypes: Array.isArray(row.mens_duty_types || row.mensDutyTypes)
      ? row.mens_duty_types || row.mensDutyTypes
      : [],
    slides,
  };
}

function readLocalTithingRecords() {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem("oikos.church.management");
    const parsed = raw ? JSON.parse(raw) : null;
    const records = Array.isArray(parsed?.tithing) ? parsed.tithing : [];
    return records
      .slice()
      .sort((a, b) =>
        String(b.collectionDate || b.collection_date || "").localeCompare(
          String(a.collectionDate || a.collection_date || "")
        )
      )
      .slice(0, 2);
  } catch (_error) {
    return [];
  }
}

async function loadRecentTithingRecords(accountId = "") {
  if (!accountId) {
    return readLocalTithingRecords();
  }

  const { data, error } = await supabase
    .from(TITHING_TABLE)
    .select("id, collection_date, cash_total, check_total, online_total, notes")
    .eq("account_id", accountId)
    .order("collection_date", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(2);

  if (error) {
    if (!isMissingRelationError(error)) {
      console.error("Live display tithing load error:", error);
    }

    return readLocalTithingRecords();
  }

  return data?.length ? data : readLocalTithingRecords();
}

function readLocalLatestAttendanceRecord() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem("oikos.church.management");
    const parsed = raw ? JSON.parse(raw) : null;
    const records = Array.isArray(parsed?.attendance) ? parsed.attendance : [];
    return records
      .slice()
      .sort((a, b) =>
        String(getAttendanceRecordDate(b)).localeCompare(String(getAttendanceRecordDate(a)))
      )[0] || null;
  } catch (_error) {
    return null;
  }
}

async function loadLatestAttendanceRecord(accountId = "") {
  if (!accountId) {
    return readLocalLatestAttendanceRecord();
  }

  const { data, error } = await supabase
    .from(ATTENDANCE_TABLE)
    .select("id, attendance_date, worship_count, visitor_count, notes")
    .eq("account_id", accountId)
    .order("attendance_date", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    if (!isMissingRelationError(error)) {
      console.error("Live display attendance load error:", error);
    }

    return readLocalLatestAttendanceRecord();
  }

  return data || readLocalLatestAttendanceRecord();
}

async function buildLoopItemsForUser(userId, branding, existingItems = [], preloaded = {}) {
  const [nextItems, serviceData, tithingRecord, attendanceRecord, serviceTimes] = await Promise.all([
    buildPreServiceLoopItemsForUser(userId, {
      churchName: branding?.name || "Fallon Church of Christ",
      churchLogoUrl: branding?.logo_url || "",
    }),
    preloaded.serviceData
      ? Promise.resolve(preloaded.serviceData)
      : loadServiceItems(userId, { hydrateSlideImages: false }),
    preloaded.tithingRecord !== undefined
      ? Promise.resolve(preloaded.tithingRecord)
      : loadRecentTithingRecords(branding?.id || ""),
    preloaded.attendanceRecord !== undefined
      ? Promise.resolve(preloaded.attendanceRecord)
      : loadLatestAttendanceRecord(branding?.id || ""),
    preloaded.serviceTimes !== undefined
      ? Promise.resolve(preloaded.serviceTimes)
      : branding?.id
        ? loadChurchServiceTimes(branding.id).catch(() => [])
        : Promise.resolve([]),
  ]);
  const preServiceHymnItems = preloaded.preServiceHymnItems || await loadPreServiceHymnItems(userId, serviceData.items || []);
  const serviceTimesItem = buildServiceTimesLoopItem(
    serviceTimes,
    (existingItems || []).find((item) => item?.id === SERVICE_TIMES_SLIDE_ID)
  );
  const preacherItem = buildPreacherLoopItem(
    branding?.integrations || {},
    (existingItems || []).find((item) => item?.id === PREACHER_SLIDE_ID)
  );
  const mensDuties = branding?.id
    ? await loadMensDutiesForAccount(branding.id).catch(() => [])
    : [];
  const mensDutiesItem = buildMensDutiesLoopItem(
    mensDuties,
    (existingItems || []).find((item) => item?.id === MENS_DUTIES_SLIDE_ID)
  );
  const membersItem = await buildMembersFamiliesLoopItem(
    branding?.id || "",
    (existingItems || []).find((item) => item?.id === MEMBERS_FAMILIES_SLIDE_ID)
  );
  const surroundingEvents = branding?.id
    ? await loadSharedChurchEventsForAccount(branding.id).catch(() => [])
    : [];
  const surroundingEventsItem = buildSurroundingChurchEventsLoopItem(surroundingEvents);

  return mergeGeneratedLoopItems(
    mergeLoopSettings(existingItems, nextItems),
    {
      serviceItems: preServiceHymnItems,
      tithingRecord,
      attendanceRecord,
      serviceTimesItem,
      preacherItem,
      mensDutiesItem,
      membersItem,
      surroundingEventsItem,
    }
  );
}

function normalizeScreen(row = {}) {
  return {
    id: row.id || row.screen_code || `screen-${Math.random().toString(36).slice(2, 8)}`,
    displayId: row.display_id || "",
    screenCode: row.screen_code || "",
    name: row.screen_name || "Unnamed screen",
    status: row.status || "connected",
    screenGroup: normalizeScreenGroup(row.screen_group || row.screenGroup),
    lastSeenAt: row.last_seen_at || "",
    deviceInfo: row.device_info || "",
  };
}

function updateLocalPublicScreen(display, screenCode, updates = {}) {
  if (typeof window === "undefined" || !display?.publicCode || !screenCode) {
    return [];
  }

  const local = loadLocalPublicDisplay(display.publicCode) || {
    display,
    screens: [],
  };
  const screens = (local.screens || []).map((screen) =>
    screen.screenCode === screenCode || screen.id === screenCode
      ? {
          ...screen,
          ...updates,
          lastSeenAt: updates.lastSeenAt || screen.lastSeenAt,
        }
      : screen
  );

  window.localStorage.setItem(
    getPublicStorageKey(display.publicCode),
    JSON.stringify({
      display: local.display || display,
      screens,
    })
  );

  return screens;
}

function saveLocalDisplay(userId, display, screens = []) {
  if (typeof window === "undefined") return;

  const existingLocalDisplay = loadLocalDisplay(userId)?.display || {};
  const existingPublicDisplay = display?.publicCode
    ? loadLocalPublicDisplay(display.publicCode)?.display || {}
    : {};
  const nextDisplay = {
    ...display,
    organizationName:
      display?.organizationName ||
      existingLocalDisplay.organizationName ||
      existingPublicDisplay.organizationName ||
      "",
    organizationLogoUrl:
      display?.organizationLogoUrl ||
      existingLocalDisplay.organizationLogoUrl ||
      existingPublicDisplay.organizationLogoUrl ||
      "",
  };

  window.localStorage.setItem(
    getStorageKey(userId),
    JSON.stringify({
      display: nextDisplay,
      screens,
    })
  );

  window.localStorage.setItem(
    getPublicStorageKey(nextDisplay.publicCode),
    JSON.stringify({
      display: nextDisplay,
      screens,
    })
  );
}

function clearLocalDisplay(userId, display = {}) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(getStorageKey(userId));

    if (display?.publicCode) {
      window.localStorage.removeItem(getPublicStorageKey(display.publicCode));
    }
  } catch (_error) {
    // Local cache cleanup is best-effort.
  }
}

function loadLocalDisplay(userId) {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getStorageKey(userId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.error("Local live display load error:", error);
    return null;
  }
}

function loadLocalPublicDisplay(code) {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getPublicStorageKey(code));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.error("Local public live display load error:", error);
    return null;
  }
}

function loadCachedChurchOrganizationBranding() {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem("oikos.organization.church");
    const account = raw ? JSON.parse(raw) : null;

    return {
      id: account?.id || "",
      name: account?.name || "",
      logo_url: account?.logo_url || account?.logoUrl || "",
    };
  } catch (_error) {
    return {};
  }
}

function findLocalDisplayBrandingByPublicCode(code) {
  if (typeof window === "undefined" || !code) {
    return {};
  }

  const normalizedCode = String(code || "").trim().toUpperCase();

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key?.startsWith(`${STORAGE_KEY_PREFIX}.`) || key.includes(".public.")) {
      continue;
    }

    try {
      const parsed = JSON.parse(window.localStorage.getItem(key) || "{}");
      const localDisplay = parsed?.display || {};
      if (String(localDisplay.publicCode || "").trim().toUpperCase() !== normalizedCode) {
        continue;
      }

      return {
        organizationName: localDisplay.organizationName || "",
        organizationLogoUrl: localDisplay.organizationLogoUrl || "",
      };
    } catch (_error) {
      // Ignore malformed local display entries.
    }
  }

  return {};
}

function enrichLocalPublicDisplayBranding(code, localPublicDisplay) {
  if (!localPublicDisplay?.display) {
    return localPublicDisplay;
  }

  const branding = findLocalDisplayBrandingByPublicCode(code);
  const cachedChurchBranding = loadCachedChurchOrganizationBranding();

  if (
    !branding.organizationName &&
    !branding.organizationLogoUrl &&
    !cachedChurchBranding.name &&
    !cachedChurchBranding.logo_url
  ) {
    return localPublicDisplay;
  }

  return {
    ...localPublicDisplay,
    display: {
      ...localPublicDisplay.display,
      organizationName:
        localPublicDisplay.display.organizationName ||
        branding.organizationName ||
        cachedChurchBranding.name ||
        "",
      organizationLogoUrl:
        localPublicDisplay.display.organizationLogoUrl ||
        branding.organizationLogoUrl ||
        cachedChurchBranding.logo_url ||
        "",
    },
  };
}

async function createLocalDisplay(userId) {
  const serviceData = await loadServiceItems(userId, { hydrateSlideImages: false });
  const branding = await fetchOrganizationBranding(userId, "church").catch(() => null);
  const tithingRecord = await loadRecentTithingRecords(branding?.id || "");
  const attendanceRecord = await loadLatestAttendanceRecord(branding?.id || "");
  const preServiceHymnItems = await loadPreServiceHymnItems(userId, serviceData.items || []);
  const base = normalizeDisplay(
    {
      id: "local-live-display",
      name: createSessionName(),
      mode: "church",
      service_id: serviceData.serviceId,
      public_code: createCode(),
      display_state: "loop",
      is_ready: false,
      is_live: false,
      pre_service_items: [],
      tithing_record: tithingRecord,
      attendance_record: attendanceRecord,
    },
    serviceData.items,
    branding || {},
    preServiceHymnItems
  );

  saveLocalDisplay(userId, base, []);
  return {
    display: base,
    screens: [],
  };
}

async function persistDisplayUpdate(actorInput, payload, options = {}) {
  const actor = normalizeLiveDisplayActor(actorInput);
  const userId = actor.userId;
  const shouldHydrate = options.hydrate !== false;
  const branding = shouldHydrate
    ? await fetchOrganizationBranding(userId, "church").catch(() => null)
    : null;
  const tithingRecord = shouldHydrate
    ? await loadRecentTithingRecords(branding?.id || "")
    : undefined;
  const attendanceRecord = shouldHydrate
    ? await loadLatestAttendanceRecord(branding?.id || "")
    : undefined;
  let nextPayload = { ...payload };

  if (!nextPayload.id) {
    const activeRow = await loadActiveDisplayRow(
      userId,
      nextPayload.account_id || actor.accountId || branding?.id || ""
    ).catch(() => null);
    if (activeRow?.id) {
      nextPayload = {
        ...nextPayload,
        id: activeRow.id,
        public_code: activeRow.public_code || nextPayload.public_code,
      };
    }
  }

  let data = null;
  let error = null;

  if (actor.isChurchOrgUser && actor.sessionToken) {
    const rpcResult = await supabase.rpc("church_org_user_upsert_live_display", {
      p_session_token: actor.sessionToken,
      p_payload: nextPayload,
    });
    data = Array.isArray(rpcResult.data) ? rpcResult.data[0] : rpcResult.data;
    error = rpcResult.error;
  } else {
    const upsertResult = await supabase
      .from(LIVE_DISPLAYS_TABLE)
      .upsert(nextPayload, {
        onConflict: "id",
      })
      .select("*")
      .single();
    data = upsertResult.data;
    error = upsertResult.error;
  }

  if (!actor.isChurchOrgUser && error && nextPayload.account_id && isMissingLiveDisplayColumnError(error, "account_id")) {
    const { account_id: _accountId, ...legacyPayload } = nextPayload;
    nextPayload = legacyPayload;
    const retry = await supabase
      .from(LIVE_DISPLAYS_TABLE)
      .upsert(nextPayload, { onConflict: "id" })
      .select("*")
      .single();
    data = retry.data;
    error = retry.error;
  }

  if (!actor.isChurchOrgUser && error && nextPayload.sleep_schedule && isMissingLiveDisplayColumnError(error, "sleep_schedule")) {
    const { sleep_schedule: _sleepSchedule, ...legacyPayload } = nextPayload;
    nextPayload = legacyPayload;
    const retry = await supabase
      .from(LIVE_DISPLAYS_TABLE)
      .upsert(nextPayload, { onConflict: "id" })
      .select("*")
      .single();
    data = retry.data;
    error = retry.error;
  }

  if (!actor.isChurchOrgUser && error && nextPayload.interactive_config && isMissingLiveDisplayColumnError(error, "interactive_config")) {
    const { interactive_config: _interactiveConfig, ...legacyPayload } = nextPayload;
    nextPayload = legacyPayload;
    const retry = await supabase
      .from(LIVE_DISPLAYS_TABLE)
      .upsert(nextPayload, { onConflict: "id" })
      .select("*")
      .single();
    data = retry.data;
    error = retry.error;
  }

  if (error && isActiveDisplayUniqueError(error)) {
    const activeRow = await loadActiveDisplayRow(
      userId,
      nextPayload.account_id || actor.accountId || branding?.id || ""
    ).catch(() => null);
    if (activeRow?.id) {
      const retryPayload = {
        ...nextPayload,
        id: activeRow.id,
        public_code: activeRow.public_code || nextPayload.public_code,
      };
      const retry = actor.isChurchOrgUser && actor.sessionToken
        ? await supabase.rpc("church_org_user_upsert_live_display", {
            p_session_token: actor.sessionToken,
            p_payload: retryPayload,
          })
        : await supabase
            .from(LIVE_DISPLAYS_TABLE)
            .upsert(retryPayload, {
              onConflict: "id",
            })
            .select("*")
            .single();
      data = retry.data;
      if (Array.isArray(data)) {
        data = data[0];
      }
      error = retry.error;
    }
  }

  if (error) {
    if (isMissingRelationError(error)) {
      const local = loadLocalDisplay(userId) || (await createLocalDisplay(userId));
      const loopServiceItems = shouldHydrate
        ? await loadPreServiceHymnItems(userId, local.display.serviceItems || [])
        : null;
      const nextDisplay = {
        ...local.display,
        ...normalizeDisplay(
          {
            ...nextPayload,
            tithing_record: tithingRecord,
            attendance_record: attendanceRecord,
          },
          local.display.serviceItems || [],
          branding || {},
          loopServiceItems
        ),
      };
      saveLocalDisplay(userId, nextDisplay, local.screens || []);
      return nextDisplay;
    }

    throw error;
  }

  const serviceItems = shouldHydrate
    ? (await loadServiceItems(userId, { hydrateSlideImages: false })).items
    : loadLocalDisplay(userId)?.display?.serviceItems || [];
  const loopServiceItems = shouldHydrate
    ? await loadPreServiceHymnItems(userId, serviceItems)
    : null;
  const display = normalizeDisplay(
    { ...data, tithing_record: tithingRecord, attendance_record: attendanceRecord },
    serviceItems,
    branding || {},
    loopServiceItems
  );
  const current = loadLocalDisplay(userId);
  saveLocalDisplay(userId, display, current?.screens || []);
  return display;
}

async function loadActiveDisplayRow(userId, accountId = "") {
  let query = supabase
    .from(LIVE_DISPLAYS_TABLE)
    .select("*")
    .eq("mode", "church")
    .eq("is_active", true)
    .order("updated_at", { ascending: false })
    .limit(1);

  query = accountId
    ? query.eq("account_id", accountId)
    : query.eq("user_id", userId);

  const { data, error } = await query;

  if (error) {
    if (isMissingRelationError(error)) {
      return null;
    }

    throw error;
  }

  return data?.[0] || null;
}

async function loadPublicDisplayBranding(displayRow = {}) {
  const accountId = displayRow.account_id || "";
  const ownerUserId = displayRow.user_id || "";

  if (!accountId && !ownerUserId) {
    return {};
  }

  let query = supabase
    .from("accounts")
    .select("id, name, logo_url, church_code, invite_code, integrations")
    .eq("type", "church")
    .limit(1);

  if (accountId) {
    query = query.eq("id", accountId);
  } else {
    query = query.eq("owner_user_id", ownerUserId);
  }

  const { data, error } = await query.maybeSingle();

  if (error || !data) {
    return {};
  }

  return data;
}

async function fastUpdateChurchLiveDisplay(actorInput, displayId, updates = {}) {
  const actor = normalizeLiveDisplayActor(actorInput);
  const userId = actor.userId;
  const brandingFallbackForLookup = await fetchOrganizationBranding(userId).catch(() => null);
  const cached = loadLocalDisplay(userId);
  const cachedDisplay = cached?.display || null;
  const activeRow =
    !displayId && cachedDisplay?.id && cachedDisplay.id !== "local-live-display"
      ? null
      : await loadActiveDisplayRow(userId, brandingFallbackForLookup?.id || "");
  const currentDisplay = cachedDisplay?.id && cachedDisplay.id !== "local-live-display"
    ? cachedDisplay
    : activeRow
      ? normalizeDisplay(activeRow, [], {})
      : null;
  const selectedServiceItemId =
    updates.selectedServiceItemId ||
    currentDisplay?.selectedServiceItemId ||
    "all-service-items";
  const liveSlides = Array.isArray(updates.liveSlides)
    ? updates.liveSlides
    : currentDisplay?.slides || [];
  const loopItems = (
    Array.isArray(updates.loopItems)
      ? updates.loopItems
      : currentDisplay?.loopItems || []
  ).filter((item) => item?.itemType !== "hymn" && item?.sourceApp !== "hymn");
  const needsBrandingFallback =
    !updates.organizationLogoUrl &&
    !currentDisplay?.organizationLogoUrl &&
    !activeRow?.organization_logo_url;
  const brandingFallback = needsBrandingFallback
    ? brandingFallbackForLookup
    : null;
  const organizationName =
    updates.organizationName ||
    currentDisplay?.organizationName ||
    activeRow?.organization_name ||
    brandingFallback?.name ||
    "";
  const organizationLogoUrl =
    updates.organizationLogoUrl ||
    currentDisplay?.organizationLogoUrl ||
    activeRow?.organization_logo_url ||
    brandingFallback?.logo_url ||
    "";
  const payload = {
    user_id: currentDisplay?.userId || activeRow?.user_id || userId,
    account_id: currentDisplay?.accountId || activeRow?.account_id || brandingFallback?.id || null,
    mode: "church",
    name: updates.name || currentDisplay?.name || createSessionName(),
    service_id: updates.serviceId || currentDisplay?.serviceId || getCurrentServiceId(),
    public_code: updates.publicCode || currentDisplay?.publicCode || createCode(),
    display_state: updates.state || currentDisplay?.state || "live",
    is_ready:
      typeof updates.isReady === "boolean" ? updates.isReady : currentDisplay?.isReady || false,
    is_live:
      typeof updates.isLive === "boolean" ? updates.isLive : currentDisplay?.isLive || false,
    selected_service_item_id: selectedServiceItemId,
    current_slide_index:
      typeof updates.currentSlideIndex === "number"
        ? updates.currentSlideIndex
        : currentDisplay?.currentSlideIndex || 0,
    live_slides: liveSlides,
    organization_name: organizationName,
    organization_logo_url: organizationLogoUrl,
    loop_interval_seconds: currentDisplay?.loopIntervalSeconds || DEFAULT_LOOP_INTERVAL_SECONDS,
    sleep_schedule: normalizeSleepSchedule(updates.sleepSchedule || currentDisplay?.sleepSchedule),
    interactive_config: normalizeInteractiveConfig(updates.interactiveConfig || currentDisplay?.interactiveConfig),
    is_active: true,
    approved_at: updates.isReady ? new Date().toISOString() : null,
    started_at: updates.isLive ? new Date().toISOString() : null,
    pre_service_items: loopItems,
  };

  const rowId = displayId || activeRow?.id || currentDisplay?.id;

  if (rowId && rowId !== "local-live-display") {
    payload.id = rowId;
  }

  const display = await persistDisplayUpdate(actorInput, payload, { hydrate: false });

  return {
    display,
    screens: cached?.screens || [],
  };
}

export async function loadChurchLiveDisplay(actorInput, options = {}) {
  const actor = normalizeLiveDisplayActor(actorInput);
  const userId = actor.userId;
  const createIfMissing = options?.createIfMissing !== false;
  const serviceData = await loadServiceItems(userId, { hydrateSlideImages: false });
  const branding = await fetchOrganizationBranding(userId, "church").catch(() => null);
  const tithingRecord = await loadRecentTithingRecords(branding?.id || "");
  const attendanceRecord = await loadLatestAttendanceRecord(branding?.id || "");

  let displayQuery = supabase
    .from(LIVE_DISPLAYS_TABLE)
    .select("*")
    .eq("mode", "church")
    .eq("is_active", true)
    .order("updated_at", { ascending: false })
    .limit(1);

  displayQuery = branding?.id
    ? displayQuery.eq("account_id", branding.id)
    : displayQuery.eq("user_id", userId);

  const { data: displayRows, error: displayError } = await displayQuery;

  if (displayError) {
    if (!isMissingRelationError(displayError)) {
      throw displayError;
    }

    const local = loadLocalDisplay(userId) || (await createLocalDisplay(userId));
    return local;
  }

  const row = displayRows?.[0];

  if (!row) {
    if (!createIfMissing) {
      clearLocalDisplay(userId);
      return {
        display: null,
        screens: [],
        serviceItems: serviceData.items || [],
      };
    }

    const selectedServiceItemId = "all-service-items";
    const loopItems = await buildLoopItemsForUser(userId, branding || {}, [], {
      serviceData,
      tithingRecord,
      attendanceRecord,
    });
    const created = await persistDisplayUpdate(userId, {
      user_id: userId,
      account_id: branding?.id || null,
      mode: "church",
      name: createSessionName(),
      service_id: serviceData.serviceId,
      public_code: createCode(),
      display_state: "loop",
      is_ready: false,
      is_live: false,
      is_active: true,
      selected_service_item_id: selectedServiceItemId,
      live_slides: buildLiveSlides(serviceData.items, selectedServiceItemId),
      organization_name: branding?.name || "",
      organization_logo_url: branding?.logo_url || "",
      loop_interval_seconds: DEFAULT_LOOP_INTERVAL_SECONDS,
      sleep_schedule: normalizeSleepSchedule(),
      interactive_config: normalizeInteractiveConfig(),
      pre_service_items: loopItems,
    });

    return {
      display: created,
      screens: [],
      serviceItems: serviceData.items || [],
    };
  }

  const selectedServiceItemId = row.selected_service_item_id || "all-service-items";
  const preServiceHymnItems = await loadPreServiceHymnItems(userId, serviceData.items || []);
  const displayBrandingPatch = {};

  if (branding?.name && row.organization_name !== branding.name) {
    displayBrandingPatch.organization_name = branding.name;
  }

  if (branding?.logo_url && row.organization_logo_url !== branding.logo_url) {
    displayBrandingPatch.organization_logo_url = branding.logo_url;
  }

  if (Object.keys(displayBrandingPatch).length > 0) {
    await supabase
      .from(LIVE_DISPLAYS_TABLE)
      .update(displayBrandingPatch)
      .eq("id", row.id)
      .then(() => null);
  }

  if (!row.account_id && branding?.id) {
    await supabase
      .from(LIVE_DISPLAYS_TABLE)
      .update({ account_id: branding.id })
      .eq("id", row.id)
      .then(() => null);
  }
  const repairedRow = {
    ...row,
    ...displayBrandingPatch,
    account_id: branding?.id || row.account_id || null,
  };
  const hydratedRow = {
    ...repairedRow,
    live_slides: isQuickLiveSource(selectedServiceItemId)
      ? repairedRow.live_slides || []
      : buildLiveSlides(
          serviceData.items,
          selectedServiceItemId
        ),
    organization_name: branding?.name || repairedRow.organization_name || "",
    organization_logo_url: branding?.logo_url || repairedRow.organization_logo_url || "",
    loop_interval_seconds: repairedRow.loop_interval_seconds || DEFAULT_LOOP_INTERVAL_SECONDS,
    sleep_schedule: normalizeSleepSchedule(repairedRow.sleep_schedule),
    pre_service_items: await buildLoopItemsForUser(userId, branding || {}, repairedRow.pre_service_items || [], {
      serviceData,
      preServiceHymnItems,
      tithingRecord,
      attendanceRecord,
    }),
    tithing_record: tithingRecord,
    attendance_record: attendanceRecord,
  };

  const display = normalizeDisplay(hydratedRow, serviceData.items, branding || {}, preServiceHymnItems);
  const screens = await loadChurchLiveScreens(display);

  saveLocalDisplay(userId, display, screens);

  return {
    display,
    screens,
    serviceItems: serviceData.items || [],
  };
}

export async function updateChurchLiveDisplay(actorInput, displayId, updates = {}) {
  const actor = normalizeLiveDisplayActor(actorInput);
  const userId = actor.userId;
  if (updates.fast === true && Array.isArray(updates.liveSlides)) {
    return fastUpdateChurchLiveDisplay(actorInput, displayId, updates);
  }

  const current = await loadChurchLiveDisplay(actorInput);
  const serviceItems = Array.isArray(current.serviceItems)
    ? current.serviceItems
    : (await loadServiceItems(userId, { hydrateSlideImages: false })).items;
  const branding = await fetchOrganizationBranding(userId, "church").catch(() => null);
  const selectedServiceItemId =
    typeof updates.selectedServiceItemId === "string"
      ? updates.selectedServiceItemId
      : current.display.selectedServiceItemId;
  const liveSlides =
    Array.isArray(updates.liveSlides)
      ? updates.liveSlides
      : isQuickLiveSource(selectedServiceItemId)
        ? current.display.slides || []
        : buildLiveSlides(serviceItems, selectedServiceItemId);
  const loopItems =
    updates.loopItems ||
    (await buildLoopItemsForUser(userId, branding || {}, current.display.loopItems || []));
  const payload = {
    id: displayId || current.display.id,
    user_id: current.display.userId || userId,
    account_id: branding?.id || current.display.accountId || null,
    mode: "church",
    name: updates.name || current.display.name,
    service_id: updates.serviceId || current.display.serviceId,
    public_code: updates.publicCode || current.display.publicCode,
    display_state: updates.state || current.display.state,
    is_ready:
      typeof updates.isReady === "boolean" ? updates.isReady : current.display.isReady,
    is_live:
      typeof updates.isLive === "boolean" ? updates.isLive : current.display.isLive,
    selected_service_item_id:
      selectedServiceItemId,
    current_slide_index:
      typeof updates.currentSlideIndex === "number"
        ? updates.currentSlideIndex
        : current.display.currentSlideIndex || 0,
    live_slides: liveSlides,
    organization_name: branding?.name || current.display.organizationName || "",
    organization_logo_url: branding?.logo_url || current.display.organizationLogoUrl || "",
    loop_interval_seconds:
      typeof updates.loopIntervalSeconds === "number"
        ? updates.loopIntervalSeconds
        : current.display.loopIntervalSeconds || DEFAULT_LOOP_INTERVAL_SECONDS,
    sleep_schedule: normalizeSleepSchedule(updates.sleepSchedule || current.display.sleepSchedule),
    interactive_config: normalizeInteractiveConfig(updates.interactiveConfig || current.display.interactiveConfig),
    is_active: true,
    approved_at: updates.isReady ? new Date().toISOString() : null,
    started_at: updates.isLive ? new Date().toISOString() : null,
    pre_service_items:
      loopItems,
  };

  const display = await persistDisplayUpdate(actorInput, payload);

  return {
    display,
    screens: current.screens,
  };
}

export async function deleteChurchLiveDisplay(userId, display = null) {
  const currentDisplay = display || loadLocalDisplay(userId)?.display || {};
  const displayId =
    currentDisplay?.id && currentDisplay.id !== "local-live-display"
      ? currentDisplay.id
      : "";

  if (displayId) {
    const { error: screensError } = await supabase
      .from(LIVE_SCREENS_TABLE)
      .delete()
      .eq("display_id", displayId);

    if (screensError && !isMissingRelationError(screensError)) {
      throw screensError;
    }

    const { error: displayError } = await supabase
      .from(LIVE_DISPLAYS_TABLE)
      .delete()
      .eq("id", displayId)
      .eq("user_id", userId);

    if (displayError && !isMissingRelationError(displayError)) {
      throw displayError;
    }
  }

  clearLocalDisplay(userId, currentDisplay);

  return {
    display: null,
    screens: [],
  };
}

export async function regenerateChurchLiveCode(userId, display) {
  return updateChurchLiveDisplay(userId, display.id, {
    publicCode: createCode(),
  });
}

export async function markChurchLiveReady(userId, display, isReady) {
  return updateChurchLiveDisplay(userId, display.id, {
    isReady,
    state: isReady ? "ready" : "loop",
    isLive: false,
  });
}

export async function startChurchServiceTakeover(userId, display) {
  return updateChurchLiveDisplay(userId, display.id, {
    state: "live",
    isReady: true,
    isLive: true,
    currentSlideIndex: 0,
  });
}

export async function returnChurchDisplayToLoop(userId, display) {
  return fastUpdateChurchLiveDisplay(userId, display.id, {
    fast: true,
    state: "loop",
    isLive: false,
    currentSlideIndex: 0,
    liveSlides: display?.slides || [],
    loopItems: display?.loopItems || [],
  });
}

export async function putChurchDisplayToSleep(userId, display) {
  return fastUpdateChurchLiveDisplay(userId, display.id, {
    fast: true,
    state: "sleep",
    isLive: false,
    currentSlideIndex: 0,
    liveSlides: display?.slides || [],
    loopItems: display?.loopItems || [],
  });
}

export async function selectChurchLiveSource(userId, display, selectedServiceItemId) {
  return updateChurchLiveDisplay(userId, display.id, {
    selectedServiceItemId,
    currentSlideIndex: 0,
  });
}

export async function quickLiveChurchHymn(userId, hymn, display = null) {
  const slides = (Array.isArray(hymn?.slides) ? hymn.slides : []).map((slide, index) => ({
    id: slide.id || `${hymn?.id || "hymn"}-quick-${index + 1}`,
    itemType: "hymn",
    title: slide.title || hymn?.title || "Untitled Hymn",
    body: slide.body || slide.text || "",
    imageUrl: slide.imageUrl || slide.image_url || "",
    imagePath: slide.imagePath || slide.image_path || "",
    storageBucket: slide.storageBucket || slide.storage_bucket || "global-hymn-files",
    songNumber: hymn?.songNumber || hymn?.song_number || "",
    parentId: hymn?.id || "",
    isEndOfSong:
      typeof slide.isEndOfSong === "boolean"
        ? slide.isEndOfSong
        : typeof slide.is_end_of_song === "boolean"
          ? slide.is_end_of_song
          : index === (Array.isArray(hymn?.slides) ? hymn.slides.length : 0) - 1,
  }));

  const currentDisplay =
    display ||
    loadLocalDisplay(userId)?.display ||
    (await loadChurchLiveDisplay(userId)).display;
  const brandingFallback = !currentDisplay?.organizationLogoUrl
    ? await fetchOrganizationBranding(userId).catch(() => null)
    : null;
  const payload = {
    user_id: userId,
    account_id: currentDisplay?.accountId || brandingFallback?.id || null,
    mode: "church",
    name: currentDisplay?.name || createSessionName(),
    service_id: currentDisplay?.serviceId || getCurrentServiceId(),
    public_code: currentDisplay?.publicCode || createCode(),
    display_state: "live",
    is_ready: true,
    is_live: true,
    is_active: true,
    selected_service_item_id: `${QUICK_HYMN_SOURCE_PREFIX}${hymn?.id || "unknown"}`,
    current_slide_index: 0,
    live_slides: slides,
    organization_name: currentDisplay?.organizationName || brandingFallback?.name || "",
    organization_logo_url: currentDisplay?.organizationLogoUrl || brandingFallback?.logo_url || "",
    loop_interval_seconds: currentDisplay?.loopIntervalSeconds || DEFAULT_LOOP_INTERVAL_SECONDS,
    sleep_schedule: normalizeSleepSchedule(currentDisplay?.sleepSchedule),
    interactive_config: normalizeInteractiveConfig(currentDisplay?.interactiveConfig),
    approved_at: new Date().toISOString(),
    started_at: new Date().toISOString(),
    pre_service_items: currentDisplay?.loopItems || [],
  };

  if (currentDisplay?.id && currentDisplay.id !== "local-live-display") {
    payload.id = currentDisplay.id;
  }

  const nextDisplay = await persistDisplayUpdate(userId, payload, { hydrate: false });
  return {
    display: nextDisplay,
    screens: loadLocalDisplay(userId)?.screens || [],
  };
}

export async function toggleChurchLoopItemVisibility(userId, display, itemId) {
  const nextLoopItems = (display?.loopItems || []).map((item) =>
    item.id === itemId
      ? {
          ...item,
          isVisible: item.isVisible === false,
        }
      : item
  );

  return updateChurchLiveDisplay(userId, display.id, {
    loopItems: nextLoopItems,
  });
}

export async function updateChurchLoopItems(userId, display, loopItems = []) {
  const nextLoopItems = (loopItems || []).map((item) => ({
    ...item,
    durationSeconds: normalizeLoopDuration(item.durationSeconds),
  }));

  return updateChurchLiveDisplay(userId, display.id, {
    loopItems: nextLoopItems,
  });
}

export async function setChurchLoopItemDuration(userId, display, itemId, durationSeconds) {
  const safeValue = normalizeLoopDuration(durationSeconds);
  const nextLoopItems = (display?.loopItems || []).map((item) =>
    item.id === itemId
      ? {
          ...item,
          durationSeconds: safeValue,
        }
      : item
  );

  return updateChurchLoopItems(userId, display, nextLoopItems);
}

export async function setChurchLoopInterval(userId, display, loopIntervalSeconds) {
  const safeValue = Math.min(Math.max(Number(loopIntervalSeconds) || DEFAULT_LOOP_INTERVAL_SECONDS, 3), 60);

  return updateChurchLiveDisplay(userId, display.id, {
    loopIntervalSeconds: safeValue,
  });
}

export async function setChurchSleepSchedule(userId, display, sleepSchedule) {
  const normalizedSchedule = normalizeSleepSchedule(sleepSchedule);
  const local = loadLocalDisplay(userId);
  const currentDisplay = display || local?.display || {};
  const nextDisplay = {
    ...currentDisplay,
    sleepSchedule: normalizedSchedule,
    updatedAt: new Date().toISOString(),
  };

  saveLocalDisplay(userId, nextDisplay, local?.screens || []);

  if (!currentDisplay?.id || currentDisplay.id === "local-live-display") {
    return {
      display: nextDisplay,
      screens: local?.screens || [],
    };
  }

  const { data, error } = await supabase
    .from(LIVE_DISPLAYS_TABLE)
    .update({
      sleep_schedule: normalizedSchedule,
      updated_at: new Date().toISOString(),
    })
    .eq("id", currentDisplay.id)
    .select("sleep_schedule, updated_at")
    .maybeSingle();

  if (error) {
    if (isMissingRelationError(error)) {
      return {
        display: nextDisplay,
        screens: local?.screens || [],
      };
    }

    throw error;
  }

  if (!data) {
    throw new Error("Could not save sleep schedule for this Live Display.");
  }

  return {
    display: {
      ...nextDisplay,
      sleepSchedule: normalizeSleepSchedule(data.sleep_schedule || normalizedSchedule),
      updatedAt: data.updated_at || nextDisplay.updatedAt,
    },
    screens: local?.screens || [],
  };
}

export async function setChurchInteractiveConfig(userId, display, interactiveConfig) {
  const normalizedConfig = normalizeInteractiveConfig(interactiveConfig);
  const local = loadLocalDisplay(userId);
  const currentDisplay = display || local?.display || {};
  const nextDisplay = {
    ...currentDisplay,
    interactiveConfig: normalizedConfig,
    updatedAt: new Date().toISOString(),
  };

  saveLocalDisplay(userId, nextDisplay, local?.screens || []);

  if (!currentDisplay?.id || currentDisplay.id === "local-live-display") {
    return {
      display: nextDisplay,
      screens: local?.screens || [],
    };
  }

  const { error } = await supabase
    .from(LIVE_DISPLAYS_TABLE)
    .update({
      interactive_config: normalizedConfig,
    })
    .eq("id", currentDisplay.id);

  if (error) {
    if (isMissingRelationError(error) || isMissingColumnError(error, "interactive_config")) {
      return {
        display: nextDisplay,
        screens: local?.screens || [],
      };
    }

    throw error;
  }

  return {
    display: nextDisplay,
    screens: local?.screens || [],
  };
}

export async function refreshChurchPreServiceLoop(userId, display) {
  return updateChurchLiveDisplay(userId, display?.id, {
    loopItems: await buildLoopItemsForUser(
      userId,
      await fetchOrganizationBranding(userId, "church").catch(() => null),
      display?.loopItems || []
    ),
  });
}

export async function goToChurchLiveSlide(userId, display, nextIndex) {
  const maxIndex = Math.max((display?.slides?.length || 1) - 1, 0);
  const safeIndex = Math.min(Math.max(nextIndex, 0), maxIndex);
  const local = loadLocalDisplay(userId);
  const nextDisplay = {
    ...display,
    currentSlideIndex: safeIndex,
    updatedAt: new Date().toISOString(),
  };

  saveLocalDisplay(userId, nextDisplay, local?.screens || []);

  if (!display?.id || display.id === "local-live-display") {
    return {
      display: nextDisplay,
      screens: local?.screens || [],
    };
  }

  const { error } = await supabase
    .from(LIVE_DISPLAYS_TABLE)
    .update({
      current_slide_index: safeIndex,
    })
    .eq("id", display.id);

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    return {
      display: nextDisplay,
      screens: local?.screens || [],
    };
  }

  return {
    display: nextDisplay,
    screens: local?.screens || [],
  };
}

export async function updateChurchLiveDisplaySlideIndex(actorInput, displayId, nextIndex) {
  const actor = normalizeLiveDisplayActor(actorInput);
  const userId = actor.userId;
  const safeIndex = Math.max(Number(nextIndex) || 0, 0);
  const branding = actor.accountId
    ? { id: actor.accountId }
    : await fetchOrganizationBranding(userId).catch(() => null);
  const activeRow = displayId ? null : await loadActiveDisplayRow(userId, branding?.id || "");
  const rowId = displayId || activeRow?.id || "";

  if (!rowId || rowId === "local-live-display") {
    const local = loadLocalDisplay(userId);
    const nextDisplay = local?.display
      ? {
          ...local.display,
          currentSlideIndex: safeIndex,
          updatedAt: new Date().toISOString(),
        }
      : null;

    if (nextDisplay) {
      saveLocalDisplay(userId, nextDisplay, local?.screens || []);
    }

    return nextDisplay;
  }

  const updatePayload = {
    id: rowId,
    account_id: activeRow?.account_id || branding?.id || null,
    current_slide_index: safeIndex,
    updated_at: new Date().toISOString(),
  };
  const { error } = actor.isChurchOrgUser && actor.sessionToken
    ? await supabase.rpc("church_org_user_upsert_live_display", {
        p_session_token: actor.sessionToken,
        p_payload: updatePayload,
      })
    : await supabase
        .from(LIVE_DISPLAYS_TABLE)
        .update({
          current_slide_index: safeIndex,
          updated_at: updatePayload.updated_at,
        })
        .eq("id", rowId);

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const local = loadLocalDisplay(userId);
    const nextDisplay = local?.display
      ? {
          ...local.display,
          currentSlideIndex: safeIndex,
          updatedAt: new Date().toISOString(),
        }
      : null;

    if (nextDisplay) {
      saveLocalDisplay(userId, nextDisplay, local?.screens || []);
    }

    return nextDisplay;
  }

  const current = loadLocalDisplay(userId);
  const nextDisplay = current?.display
    ? {
        ...current.display,
        currentSlideIndex: safeIndex,
        updatedAt: new Date().toISOString(),
      }
    : activeRow
      ? normalizeDisplay(
          {
            ...activeRow,
            current_slide_index: safeIndex,
            updated_at: new Date().toISOString(),
          },
          [],
          {}
        )
      : null;

  if (nextDisplay) {
    saveLocalDisplay(userId, nextDisplay, current?.screens || []);
  }

  return nextDisplay;
}

export async function loadPublicChurchLiveDisplay(publicCode) {
  const normalizedPublicCode = String(publicCode || "").trim();
  const localPublicDisplay =
    enrichLocalPublicDisplayBranding(
      normalizedPublicCode,
      loadLocalPublicDisplay(normalizedPublicCode) ||
        loadLocalPublicDisplay(normalizedPublicCode.toUpperCase())
    );

  const { data: displayRow, error } = await supabase
    .from(LIVE_DISPLAYS_TABLE)
    .select("*")
    .ilike("public_code", normalizedPublicCode)
    .eq("is_active", true)
    .maybeSingle();

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    if (!localPublicDisplay) {
      return null;
    }

    return localPublicDisplay;
  }

  if (!displayRow) {
    return localPublicDisplay;
  }

  const [publicLoopHymnItems, publicBranding] = await Promise.all([
    loadPublicPreServiceHymnItems(normalizedPublicCode, []),
    loadPublicDisplayBranding(displayRow),
  ]);
  const accountId = displayRow.account_id || publicBranding?.id || "";
  const publicServiceTimes = accountId
    ? await loadChurchServiceTimes(accountId).catch(() => [])
    : [];
  const publicTithingRecord = accountId
    ? await loadRecentTithingRecords(accountId).catch(() => [])
    : [];
  const publicAttendanceRecord = accountId
    ? await loadLatestAttendanceRecord(accountId).catch(() => null)
    : null;
  const existingLoopItems = Array.isArray(displayRow.pre_service_items)
    ? displayRow.pre_service_items
    : [];
  const logoLoopItem = buildPreServiceLoopItems({
    churchName: publicBranding?.name || displayRow.organization_name || "Church",
    churchLogoUrl: publicBranding?.logo_url || displayRow.organization_logo_url || "",
  }).find((loopItem) => loopItem.id === "logo-slide");
  const membersItem = await buildMembersFamiliesLoopItem(
    accountId,
    existingLoopItems.find((loopItem) => loopItem?.id === MEMBERS_FAMILIES_SLIDE_ID),
    { publicCode: normalizedPublicCode }
  );
  const serviceTimesItem = buildServiceTimesLoopItem(
    publicServiceTimes,
    existingLoopItems.find((loopItem) => loopItem?.id === SERVICE_TIMES_SLIDE_ID)
  );
  const preacherItem = buildPreacherLoopItem(
    publicBranding?.integrations || {},
    existingLoopItems.find((loopItem) => loopItem?.id === PREACHER_SLIDE_ID)
  );
  const publicMensDuties = accountId
    ? await loadMensDutiesForAccount(accountId).catch(() => [])
    : [];
  const publicMensDutyTypes = accountId
    ? await loadMensDutyTypesForAccount(accountId).catch(() => [])
    : [];
  const mensDutiesItem = buildMensDutiesLoopItem(
    publicMensDuties,
    existingLoopItems.find((loopItem) => loopItem?.id === MENS_DUTIES_SLIDE_ID)
  );
  const publicSurroundingEvents = accountId
    ? await loadSharedChurchEventsForAccount(accountId).catch(() => [])
    : [];
  const surroundingEventsItem = buildSurroundingChurchEventsLoopItem(publicSurroundingEvents);
  const publicLoopItems = mergeGeneratedLoopItems(
    mergeLoopSettings(existingLoopItems, logoLoopItem ? [logoLoopItem] : []),
    {
      serviceItems: publicLoopHymnItems,
      tithingRecord: publicTithingRecord,
      attendanceRecord: publicAttendanceRecord,
      serviceTimesItem,
      preacherItem,
      mensDutiesItem,
      membersItem,
      surroundingEventsItem,
    }
  );
  const display = normalizeDisplay(
    {
      ...displayRow,
      organization_name: displayRow.organization_name || publicBranding?.name || "",
      organization_logo_url: displayRow.organization_logo_url || publicBranding?.logo_url || "",
      account_id: accountId || null,
      pre_service_items: publicLoopItems,
      tithing_record: publicTithingRecord,
      attendance_record: publicAttendanceRecord,
      service_times: publicServiceTimes,
      mens_duty_types: publicMensDutyTypes,
    },
    [],
    publicBranding || {},
    publicLoopHymnItems
  );

  const { data: screensRows } = await supabase
    .from(LIVE_SCREENS_TABLE)
    .select("*")
    .eq("display_id", displayRow.id)
    .order("last_seen_at", { ascending: false });

  return {
    display,
    screens: Array.isArray(screensRows)
      ? getManageableScreens(screensRows)
      : [],
  };
}

export function normalizePublicChurchLiveDisplayRow(row = {}, currentDisplay = {}) {
  if (!row?.id) return null;

  const currentSlides = Array.isArray(currentDisplay?.slides) ? currentDisplay.slides : [];
  const rowLiveSlides = Array.isArray(row.live_slides) ? row.live_slides : [];
  const liveSlides =
    row.display_state === "live" && rowLiveSlides.length === 0 && currentSlides.length > 0
      ? currentSlides
      : row.live_slides;
  const branding = {
    id: row.account_id || currentDisplay?.accountId || "",
    name: row.organization_name || currentDisplay?.organizationName || "",
    logo_url: row.organization_logo_url || currentDisplay?.organizationLogoUrl || "",
    church_code: currentDisplay?.organizationLoginCode || "",
  };

  return normalizeDisplay(
    {
      ...row,
      live_slides: liveSlides,
      organization_name: row.organization_name || currentDisplay?.organizationName || "",
      organization_logo_url: row.organization_logo_url || currentDisplay?.organizationLogoUrl || "",
    },
    [],
    branding,
    null
  );
}

export function subscribeToPublicChurchLiveDisplay(publicCode, onChange) {
  const normalizedPublicCode = String(publicCode || "").trim();
  const realtimePublicCode = normalizedPublicCode.toUpperCase();

  if (!normalizedPublicCode || typeof onChange !== "function") {
    return () => {};
  }

  const channel = supabase
    .channel(`church-live-display:${normalizedPublicCode}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: LIVE_DISPLAYS_TABLE,
        filter: `public_code=eq.${realtimePublicCode}`,
      },
      (payload) => {
        const row = payload?.new || null;
        if (!row?.id || String(row.public_code || "").toLowerCase() !== normalizedPublicCode.toLowerCase()) {
          return;
        }

        onChange(row);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

export function broadcastChurchLiveDisplayControl(publicCode, payload = {}) {
  const code = normalizeLiveDisplayControlCode(publicCode);
  const channelName = getLiveDisplayControlChannelName(code);

  if (!channelName) return () => {};

  let closed = false;
  const channel = supabase.channel(channelName, {
    config: {
      broadcast: {
        self: false,
      },
    },
  });

  function closeChannel(delayMs = 0) {
    if (closed) return;
    closed = true;

    if (delayMs > 0 && typeof window !== "undefined") {
      window.setTimeout(() => supabase.removeChannel(channel), delayMs);
      return;
    }

    supabase.removeChannel(channel);
  }

  channel.subscribe((status) => {
    if (closed || status !== "SUBSCRIBED") return;

    channel
      .send({
        type: "broadcast",
        event: LIVE_DISPLAY_CONTROL_EVENT,
        payload: {
          ...payload,
          publicCode: code,
          sentAt: Date.now(),
        },
      })
      .catch((error) => {
        console.error("Live display control broadcast error:", error);
      })
      .finally(() => {
        closeChannel(150);
      });
  });

  return () => closeChannel();
}

export function createChurchLiveDisplayControlBroadcaster(publicCode) {
  const code = normalizeLiveDisplayControlCode(publicCode);
  const channelName = getLiveDisplayControlChannelName(code);

  if (!channelName) {
    return {
      send: () => false,
      close: () => {},
    };
  }

  let closed = false;
  let isReady = false;
  let queuedPayload = null;
  const channel = supabase.channel(channelName, {
    config: {
      broadcast: {
        self: false,
      },
    },
  });

  function sendNow(payload = {}) {
    channel
      .send({
        type: "broadcast",
        event: LIVE_DISPLAY_CONTROL_EVENT,
        payload: {
          ...payload,
          publicCode: code,
          sentAt: Date.now(),
        },
      })
      .catch((error) => {
        console.error("Live display control broadcast error:", error);
      });
  }

  channel.subscribe((status) => {
    if (closed) return;

    if (status === "SUBSCRIBED") {
      isReady = true;
      if (queuedPayload) {
        const nextPayload = queuedPayload;
        queuedPayload = null;
        sendNow(nextPayload);
      }
    }
  });

  return {
    send(payload = {}) {
      if (closed) return false;

      if (!isReady) {
        queuedPayload = payload;
        return false;
      }

      sendNow(payload);
      return true;
    },
    close() {
      if (closed) return;
      closed = true;
      supabase.removeChannel(channel);
    },
  };
}

export function subscribeToChurchLiveDisplayControl(publicCode, onMessage) {
  const code = normalizeLiveDisplayControlCode(publicCode);
  const channelName = getLiveDisplayControlChannelName(code);

  if (!channelName || typeof onMessage !== "function") {
    return () => {};
  }

  const channel = supabase
    .channel(channelName, {
      config: {
        broadcast: {
          self: false,
        },
      },
    })
    .on("broadcast", { event: LIVE_DISPLAY_CONTROL_EVENT }, ({ payload }) => {
      if (String(payload?.publicCode || "").trim().toUpperCase() !== code) return;
      onMessage(payload || {});
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

export async function registerChurchLiveScreen(display, screenName, deviceInfo, screenGroup = "service") {
  const sessionKey = `${SCREEN_SESSION_PREFIX}.${display.id}`;
  const deviceToken = getDeviceToken(display.id);
  const safeScreenGroup = normalizeScreenGroup(screenGroup);
  const screenCode = getScreenCode(display.id, deviceToken, safeScreenGroup);
  const { data: existingScreen, error: existingScreenError } = await supabase
    .from(LIVE_SCREENS_TABLE)
    .select("*")
    .eq("screen_code", screenCode)
    .maybeSingle();
  const existingScreenName =
    !existingScreenError && existingScreen?.screen_name
      ? existingScreen.screen_name
      : "";
  const nextScreenName = existingScreenName && !isGenericScreenName(existingScreenName)
    ? existingScreenName
    : await createUniqueScreenName(display.id, safeScreenGroup, screenCode, screenName);
  const payload = {
    display_id: display.id,
    screen_code: screenCode,
    screen_name: nextScreenName || getDefaultScreenNamePrefix(safeScreenGroup),
    status: !existingScreenError && existingScreen?.status === "identify" ? "identify" : "connected",
    screen_group: safeScreenGroup,
    device_info: deviceInfo || "",
    last_seen_at: new Date().toISOString(),
  };

  let { data, error } = await supabase
    .from(LIVE_SCREENS_TABLE)
    .upsert(payload, {
      onConflict: "screen_code",
    })
    .select("*")
    .single();

  if (error && isMissingColumnError(error, "screen_group")) {
    throw new Error(
      "The church_live_screens.screen_group column is missing or not visible to Supabase yet. Run sql/fix-church-live-screen-groups.sql, then reload the Supabase schema cache."
    );
  }

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const local = loadLocalPublicDisplay(display.publicCode) || {
      display,
      screens: [],
    };
    const existingScreens = local.screens || [];
    const nextScreen = normalizeScreen({
      ...payload,
      id: screenCode,
      screen_name:
        existingScreens.find((screen) => screen.screenCode === screenCode)?.name ||
        payload.screen_name,
    });
    const screens = existingScreens.some((screen) => screen.screenCode === nextScreen.screenCode)
      ? existingScreens.map((screen) =>
          screen.screenCode === nextScreen.screenCode ? nextScreen : screen
        )
      : [...existingScreens, nextScreen];

    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(sessionKey, nextScreen.id);
      window.localStorage.setItem(
        getPublicStorageKey(display.publicCode),
        JSON.stringify({
          display,
          screens,
        })
      );
    }

    return nextScreen;
  }

  const nextScreen = normalizeScreen(data);

  if (typeof window !== "undefined" && nextScreen.id) {
    window.sessionStorage.setItem(sessionKey, nextScreen.id);
  }

  return nextScreen;
}

export async function updateChurchLiveScreenName(display, screenCode, screenName) {
  const safeName = String(screenName || "").trim() || "Live screen";
  const payload = {
    screen_name: safeName,
    last_seen_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from(LIVE_SCREENS_TABLE)
    .update(payload)
    .eq("screen_code", screenCode)
    .select("*")
    .single();

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    updateLocalPublicScreen(display, screenCode, {
      name: safeName,
      lastSeenAt: payload.last_seen_at,
    });

    return {
      display,
      screens: getManageableScreens(loadLocalPublicDisplay(display.publicCode)?.screens || []),
    };
  }

  return {
    display,
    screens: getManageableScreens([data, ...(loadLocalPublicDisplay(display.publicCode)?.screens || [])]),
  };
}

export async function updateChurchLiveScreenGroup(display, screenCode, screenGroup) {
  const safeGroup = normalizeScreenGroup(screenGroup);
  const payload = {
    screen_group: safeGroup,
    last_seen_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from(LIVE_SCREENS_TABLE)
    .update(payload)
    .eq("screen_code", screenCode)
    .select("*")
    .single();

  if (error) {
    if (!isMissingRelationError(error) && !isMissingColumnError(error, "screen_group")) {
      throw error;
    }

    updateLocalPublicScreen(display, screenCode, {
      screenGroup: safeGroup,
      lastSeenAt: payload.last_seen_at,
    });

    return {
      display,
      screens: getManageableScreens(loadLocalPublicDisplay(display.publicCode)?.screens || []),
    };
  }

  return {
    display,
    screens: getManageableScreens([data, ...(loadLocalPublicDisplay(display.publicCode)?.screens || [])]),
  };
}

export async function identifyChurchLiveScreen(display, screenCode) {
  const payload = {
    status: "identify",
    last_seen_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from(LIVE_SCREENS_TABLE)
    .update(payload)
    .eq("screen_code", screenCode);

  if (error && !isMissingRelationError(error)) {
    throw error;
  }

  updateLocalPublicScreen(display, screenCode, {
    status: "identify",
    lastSeenAt: payload.last_seen_at,
  });

  return {
    display,
    screens: getManageableScreens(loadLocalPublicDisplay(display.publicCode)?.screens || []),
  };
}

export async function clearChurchLiveScreenIdentify(display, screenCode) {
  const payload = {
    status: "connected",
    last_seen_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from(LIVE_SCREENS_TABLE)
    .update(payload)
    .eq("screen_code", screenCode);

  if (error && !isMissingRelationError(error)) {
    throw error;
  }

  updateLocalPublicScreen(display, screenCode, {
    status: "connected",
    lastSeenAt: payload.last_seen_at,
  });

  return true;
}

export async function loadChurchLiveScreenStatus(display, screenCode) {
  if (!screenCode) {
    return null;
  }

  const seenAt = new Date().toISOString();
  const { data, error } = await supabase
    .from(LIVE_SCREENS_TABLE)
    .update({ last_seen_at: seenAt })
    .eq("screen_code", screenCode)
    .select("*")
    .maybeSingle();

  if (error) {
    if (!isMissingRelationError(error)) {
      throw error;
    }

    const localScreen = (loadLocalPublicDisplay(display?.publicCode)?.screens || []).find(
      (screen) => screen.screenCode === screenCode || screen.id === screenCode
    ) || null;

    if (!localScreen) {
      return null;
    }

    updateLocalPublicScreen(display, screenCode, {
      lastSeenAt: seenAt,
    });

    return {
      ...localScreen,
      lastSeenAt: seenAt,
    };
  }

  if (!data) {
    return null;
  }

  const nextScreen = normalizeScreen(data);

  updateLocalPublicScreen(display, screenCode, {
    lastSeenAt: nextScreen.lastSeenAt || seenAt,
    status: nextScreen.status,
  });

  return nextScreen;
}

export async function removeChurchLiveScreen(display, screenCode, screenId = "") {
  const identifiers = Array.from(new Set([screenCode, screenId].map((value) => String(value || "").trim()).filter(Boolean)));
  const uuidIdentifiers = identifiers.filter((value) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
  );

  if (identifiers.length === 0) {
    return {
      display,
      screens: await loadChurchLiveScreens(display),
    };
  }

  const deleteByScreenCode = await supabase
    .from(LIVE_SCREENS_TABLE)
    .delete()
    .eq("display_id", display?.id || "")
    .in("screen_code", identifiers);

  if (deleteByScreenCode.error && !isMissingRelationError(deleteByScreenCode.error)) {
    throw deleteByScreenCode.error;
  }

  if (uuidIdentifiers.length > 0) {
    const deleteById = await supabase
      .from(LIVE_SCREENS_TABLE)
      .delete()
      .eq("display_id", display?.id || "")
      .in("id", uuidIdentifiers);

    if (deleteById.error && !isMissingRelationError(deleteById.error)) {
      throw deleteById.error;
    }
  }

  let localScreens = [];

  if (typeof window !== "undefined" && display?.publicCode) {
    const local = loadLocalPublicDisplay(display.publicCode) || {
      display,
      screens: [],
    };
    localScreens = (local.screens || []).filter((screen) =>
      !identifiers.includes(screen.screenCode) && !identifiers.includes(screen.id)
    );

    window.localStorage.setItem(
      getPublicStorageKey(display.publicCode),
      JSON.stringify({
        display: local.display || display,
        screens: localScreens,
      })
    );
  }

  return {
    display,
    screens: await loadChurchLiveScreens(display),
  };
}
